import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactGridLayout, { WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import {
  AreaChart, Area, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import {
  getDashboards,
  getDashboard,
  createDashboard,
  updateDashboard,
  deleteDashboard,
  getDashboardTemplates,
  createDashboardTemplate,
  deleteDashboardTemplate,
  getWidgetData,
  getConsumptionData,
  getGpsMapData,
  getDeviceMetrics,
  getDevices,
  getAlertLogs,
  getWidgetAlarms,
  evaluateWidgetAlarms,
  acknowledgeWidgetAlarm,
} from '../services/api';
import {
  AlertLog,
  DashboardSummary,
  DashboardTemplate,
  DashboardWidget,
  DashboardWidgetType,
  ConsumptionDataResponse,
  Device,
  DeviceMetric,
  RuntimeCondition,
  TenantPlan,
  User,
  WidgetAlarm,
  WidgetConfig,
  WidgetDataResponse,
} from '../types';
import { useIsMobile } from '../hooks/useIsMobile';
import { useAlarmAudio } from '../hooks/useAlarmAudio';

const GridLayout = WidthProvider(ReactGridLayout);

const WIDGET_TYPES: { type: DashboardWidgetType; label: string; summary: string }[] = [
  { type: 'kpi', label: 'KPI', summary: 'Single live value with threshold context.' },
  { type: 'timeseries', label: 'Time Series', summary: 'Trends, thresholds, states, and buckets.' },
  { type: 'consumption', label: 'Consumption Analysis', summary: 'Usage totals by day, week, or month.' },
  { type: 'gauge', label: 'Gauge', summary: 'Process value against an operating range.' },
  { type: 'tank', label: 'Tank Level', summary: 'Animated liquid level and capacity view.' },
  { type: 'gps_map', label: 'GPS Map', summary: 'Asset position, trail, and movement history.' },
  { type: 'state', label: 'State', summary: 'Current ON/OFF, running, or fault condition.' },
  { type: 'alerts', label: 'Alerts', summary: 'Recent alarm activity inside the dashboard.' },
  { type: 'status', label: 'Device Status', summary: 'Fleet health summary and device list.' },
  { type: 'text', label: 'Text', summary: 'Operator notes and static dashboard context.' },
];

const TYPE_LABELS: Record<DashboardWidgetType, string> = {
  kpi: 'KPI',
  timeseries: 'Time Series',
  consumption: 'Consumption Analysis',
  gauge: 'Gauge',
  tank: 'Tank Level',
  gps_map: 'GPS Map',
  state: 'State',
  alerts: 'Alerts',
  status: 'Device Status',
  text: 'Text',
};

type TimeSeriesChartMode = NonNullable<WidgetConfig['chart_mode']>;

const TIME_SERIES_STYLE_OPTIONS: {
  mode: TimeSeriesChartMode;
  label: string;
  eyebrow: string;
  preview: 'line' | 'threshold' | 'step' | 'bar' | 'runtime';
  description: string;
  bullets: string[];
}[] = [
  {
    mode: 'line',
    label: 'Metric trend line',
    eyebrow: 'Numeric history',
    preview: 'line',
    description: 'Plots selected device metrics over time so operators can compare drift, spikes, and process movement.',
    bullets: ['Multiple devices on one chart', 'Per-device metric binding', 'Best for level, pressure, flow, power, and temperature'],
  },
  {
    mode: 'threshold_trend',
    label: 'Threshold trend',
    eyebrow: 'Alarm bands',
    preview: 'threshold',
    description: 'Shows the metric trend with warn and critical guide lines so limit breaches are visible at a glance.',
    bullets: ['Editable warn and critical values', 'Above or below threshold direction', 'Best for alarms, compliance, and operating limits'],
  },
  {
    mode: 'step_state',
    label: 'Step state trend',
    eyebrow: 'Discrete changes',
    preview: 'step',
    description: 'Draws changes as steps instead of smooth curves, which is clearer for digital states and staged equipment.',
    bullets: ['Great for 0/1, ON/OFF, and stage values', 'Preserves sudden transitions', 'Best for breakers, pumps, valves, and generator status'],
  },
  {
    mode: 'bucket_bar',
    label: 'Bucket bar trend',
    eyebrow: 'Grouped totals',
    preview: 'bar',
    description: 'Groups readings into hourly or daily bars so teams can compare usage, volume, and activity by period.',
    bullets: ['Hourly buckets for short ranges', 'Daily buckets for weekly/monthly ranges', 'Best for litres, kWh, runtime counts, and message volume'],
  },
  {
    mode: 'runtime_histogram',
    label: 'Condition runtime histogram',
    eyebrow: 'State duration',
    preview: 'runtime',
    description: 'Turns readings into labelled states and totals how many hours each state occupied in every bucket.',
    bullets: ['Editable ON/OFF/RUNNING/FAULT rules', 'Fallback and No telemetry handling', 'Best for generators, pumps, breakers, and compressors'],
  },
];

const SERIES_COLORS = ['#2DD4A8', '#3b82f6', '#F5A623', '#e8602c', '#a78bfa', '#f472b6', '#22d3ee', '#84cc16'];

const SINGLE_DEVICE_TYPES: DashboardWidgetType[] = ['kpi', 'consumption', 'gauge', 'tank', 'state'];
const METRIC_TYPES: DashboardWidgetType[] = ['kpi', 'timeseries', 'consumption', 'gauge', 'tank', 'state'];

function clamp(v: number, min: number, max: number) {
  return Math.min(max, Math.max(min, v));
}

function formatDuration(seconds?: number): string {
  if (seconds == null || isNaN(seconds)) return '';
  const s = Math.max(0, Math.floor(seconds));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function formatLatestFreshness(latest?: { age_seconds?: number | null; stale?: boolean; stable?: boolean }): string {
  if (!latest) return '';
  const age = typeof latest.age_seconds === 'number' && Number.isFinite(latest.age_seconds)
    ? formatDuration(latest.age_seconds)
    : '';
  if (latest.stale) return age ? `stale - ${age} old` : 'stale reading';
  if (latest.stable === false) return age ? `updating - ${age} old` : 'updating';
  return '';
}

function formatValue(v: number | string | null | undefined): string {
  if (v == null) return '—';
  if (typeof v === 'number') return Number.isInteger(v) ? String(v) : v.toFixed(1);
  return String(v);
}

function formatAxisTick(v: number | string): string {
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  if (Object.is(n, -0) || n === 0) return '0';

  const abs = Math.abs(n);
  if (abs >= 1_000_000) {
    const value = n / 1_000_000;
    return `${Number.isInteger(value) ? value.toFixed(0) : value.toFixed(1)}M`;
  }
  if (abs >= 100_000) {
    const value = n / 1_000;
    return `${Number.isInteger(value) ? value.toFixed(0) : value.toFixed(1)}k`;
  }
  if (abs >= 100) return Math.round(n).toLocaleString();
  if (abs >= 10) return Number.isInteger(n) ? String(n) : n.toFixed(1);
  if (abs >= 1) return Number.isInteger(n) ? String(n) : n.toFixed(1);
  return n.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
}

function getMetricAxisDomain(values: number[]): [number, number] | undefined {
  if (!values.length) return undefined;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = Math.max(max - min, 0);
  const scale = Math.max(Math.abs(min), Math.abs(max), 1);
  const padding = Math.max(span * 0.15, scale * 0.08);
  const lower = min >= 0 ? Math.max(0, min - padding) : min - padding;
  return [lower, max + padding];
}

type RuntimeBucketSize = 'hour' | 'day';
type RuntimeBehavior = 'fallback' | 'unknown';

interface RuntimeResolvedState {
  key: string;
  label: string;
  color: string;
  operator?: RuntimeCondition['operator'];
  value?: string;
  kind: 'condition' | 'fallback' | 'unknown';
}

interface RuntimeStateModel {
  conditions: RuntimeResolvedState[];
  fallback: RuntimeResolvedState;
  unknown: RuntimeResolvedState;
  allStates: RuntimeResolvedState[];
  unmatchedBehavior: RuntimeBehavior;
}

const RUNTIME_STATE_COLORS = ['#30D158', '#FF9F0A', '#0A84FF', '#A78BFA', '#22D3EE', '#F472B6'];

function getRuntimeBucketSize(rangeHours: number, configured?: WidgetConfig['bucket_size']): RuntimeBucketSize {
  if (configured === 'hour' || configured === 'day') return configured;
  return rangeHours <= 48 ? 'hour' : 'day';
}

function floorRuntimeBucket(date: Date, bucketSize: RuntimeBucketSize): Date {
  const d = new Date(date);
  d.setMinutes(0, 0, 0);
  if (bucketSize === 'day') d.setHours(0, 0, 0, 0);
  return d;
}

function addRuntimeBucket(date: Date, bucketSize: RuntimeBucketSize): Date {
  const d = new Date(date);
  if (bucketSize === 'day') d.setDate(d.getDate() + 1);
  else d.setHours(d.getHours() + 1);
  return d;
}

function formatRuntimeBucketLabel(date: Date, bucketSize: RuntimeBucketSize): string {
  return bucketSize === 'day'
    ? date.toLocaleDateString([], { month: 'short', day: '2-digit' })
    : date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function getTrendBucketSize(rangeHours: number): RuntimeBucketSize {
  return rangeHours <= 48 ? 'hour' : 'day';
}

function aggregateTrendValues(values: number[], aggregation: WidgetConfig['aggregation'] = 'avg'): number | undefined {
  if (!values.length) return undefined;
  if (aggregation === 'min') return Math.min(...values);
  if (aggregation === 'max') return Math.max(...values);
  if (aggregation === 'last') return values[values.length - 1];
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function buildBucketTrendRows(
  series: Array<{ time: string; value?: number; series_key?: string; device_id: string }>,
  seriesKeys: string[],
  rangeHours: number,
  aggregation: WidgetConfig['aggregation'] = 'avg',
) {
  const bucketSize = getTrendBucketSize(rangeHours);
  const buckets = new Map<string, {
    sort: number;
    label: string;
    values: Record<string, number[]>;
  }>();

  series.forEach((point) => {
    if (typeof point.value !== 'number' || !Number.isFinite(point.value)) return;
    const sourceTime = new Date(point.time);
    if (Number.isNaN(sourceTime.getTime())) return;
    const bucket = floorRuntimeBucket(sourceTime, bucketSize);
    const key = bucket.toISOString();
    const row = buckets.get(key) || {
      sort: bucket.getTime(),
      label: formatRuntimeBucketLabel(bucket, bucketSize),
      values: {},
    };
    const seriesKey = point.series_key || point.device_id;
    row.values[seriesKey] = [...(row.values[seriesKey] || []), point.value];
    buckets.set(key, row);
  });

  return Array.from(buckets.values())
    .sort((a, b) => a.sort - b.sort)
    .map((bucket) => {
      const row: Record<string, any> = { label: bucket.label };
      seriesKeys.forEach((key) => {
        const value = aggregateTrendValues(bucket.values[key] || [], aggregation);
        if (value != null) row[key] = Number(value.toFixed(3));
      });
      return row;
    });
}

type ConsumptionMode = NonNullable<WidgetConfig['consumption_mode']>;

interface ConsumptionBucketRow {
  time: string;
  label: string;
  consumed: number;
  refill: number;
  refillEvents: number;
  resetEvents: number;
  pointCount: number;
  startValue?: number;
  endValue?: number;
  bucketStart: number;
  bucketEnd: number;
  anomaly?: boolean;
  anomalyReasons: string[];
  coveragePct?: number;
  gapMinutes?: number;
}

interface ConsumptionAnalysis {
  mode: ConsumptionMode;
  rows: ConsumptionBucketRow[];
  totalConsumed: number;
  refillVolume: number;
  refillEvents: number;
  resetEvents: number;
  startValue?: number;
  endValue?: number;
  startTime?: string;
  endTime?: string;
  avgPerHour: number;
  projectedEmptyHours?: number;
  pointCount: number;
  requestedFrom: number;
  requestedTo: number;
  coverageStart?: number;
  coverageEnd?: number;
  coverageHours: number;
  coverageRatio: number;
  isSampleCapped: boolean;
  hasPartialCoverage: boolean;
  peakBucket?: ConsumptionBucketRow;
  warnings: string[];
  anomalyCount: number;
}

const CONSUMPTION_MODE_OPTIONS: { mode: ConsumptionMode; label: string; summary: string }[] = [
  {
    mode: 'tank_drawdown',
    label: 'Tank drawdown',
    summary: 'Treats the metric as fuel remaining. Drops count as consumption; rises count as refills.',
  },
  {
    mode: 'cumulative_counter',
    label: 'Cumulative counter',
    summary: 'Treats the metric as total fuel used. Positive increments count as consumption; drops are resets.',
  },
  {
    mode: 'rate_total',
    label: 'Rate / flow total',
    summary: 'Treats the metric as a live flow rate and integrates it across the selected window.',
  },
];

function getConsumptionModeLabel(mode?: WidgetConfig['consumption_mode']) {
  return CONSUMPTION_MODE_OPTIONS.find((option) => option.mode === (mode || 'tank_drawdown'))?.label || 'Tank drawdown';
}

function getConsumptionRateModeWarning(cfg: WidgetConfig): string | null {
  if ((cfg.consumption_mode || 'tank_drawdown') !== 'rate_total') return null;

  const metricLabel = (cfg.metric || '').trim();
  const nameText = [cfg.metric, cfg.title].filter(Boolean).join(' ').replace(/[_-]+/g, ' ');
  const contextText = [cfg.metric, cfg.title, cfg.unit].filter(Boolean).join(' ').replace(/[_-]+/g, ' ');
  const ratePattern = /\b(flow|flowrate|rate|throughput|lph|lpm|lps|gph|gpm|kw)\b|(?:\/\s*(?:h|hr|hour|min|minute|m|s|sec|second)\b)|(?:\bper\s+(?:hour|hr|minute|min|second|sec)\b)/i;
  const levelPattern = /\b(tank|level|remaining|balance|volume)\b|\bfuel\s+(?:value|level|remaining|volume)\b/i;
  const looksLikeRate = ratePattern.test(contextText);
  const looksLikeLevel = levelPattern.test(nameText);

  if (looksLikeRate && !looksLikeLevel) return null;

  const metricCopy = metricLabel ? `${metricLabel} does not look like a flow/rate metric.` : 'Rate / flow total needs a flow/rate metric.';
  return `${metricCopy} Use Tank drawdown for tank level or remaining fuel readings; rate mode multiplies the metric by elapsed time and can show impossible totals when pointed at a level value.`;
}

function formatConsumptionAmount(value: number): string {
  const abs = Math.abs(value);
  const maximumFractionDigits = abs >= 100 ? 0 : abs >= 10 ? 1 : 2;
  return value.toLocaleString([], { maximumFractionDigits });
}

function formatConsumptionDate(timestamp?: number): string {
  if (!timestamp) return 'n/a';
  return new Date(timestamp).toLocaleString([], {
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getConsumptionBucketSize(rangeHours: number, configured?: WidgetConfig['bucket_size']): 'hour' | 'day' {
  if (configured === 'hour' || configured === 'day') return configured;
  return rangeHours <= 48 ? 'hour' : 'day';
}

function formatConsumptionAnomalyReason(reason: string): string {
  const labels: Record<string, string> = {
    low_coverage: 'low coverage',
    long_gap: 'long telemetry gap',
    exceeds_capacity: 'above tank capacity',
    exceeds_daily_limit: 'above daily limit',
    invalid_value: 'invalid readings',
  };
  return labels[reason] || reason.replace(/_/g, ' ');
}

function formatConsumptionAnomalySummary(rows: ConsumptionBucketRow[]): string {
  const flaggedRows = rows.filter((row) => row.anomaly);
  if (!flaggedRows.length) return 'No flags';
  const reasons = Array.from(new Set(flaggedRows.flatMap((row) => row.anomalyReasons)))
    .map(formatConsumptionAnomalyReason);
  const reasonCopy = reasons.length ? `: ${reasons.slice(0, 3).join(', ')}` : '';
  return `${flaggedRows.length} flagged${reasonCopy}`;
}

function buildBackendConsumptionAnalysis(
  response: ConsumptionDataResponse,
  cfg: WidgetConfig,
): ConsumptionAnalysis {
  const mode = cfg.consumption_mode || 'tank_drawdown';
  const rangeHours = cfg.range_hours || 24;
  const requestedTo = Date.now();
  const requestedFrom = requestedTo - rangeHours * 60 * 60 * 1000;
  const bucketSize = getConsumptionBucketSize(rangeHours, cfg.bucket_size);
  const coverageStart = response.summary.coverage_start
    ? new Date(response.summary.coverage_start).getTime()
    : undefined;
  const coverageEnd = response.summary.coverage_end
    ? new Date(response.summary.coverage_end).getTime()
    : undefined;
  const coverageHours = coverageStart != null && coverageEnd != null
    ? Math.max((coverageEnd - coverageStart) / 36e5, 0)
    : 0;
  const coverageRatio = coverageHours / Math.max(rangeHours, 1);
  const rows = response.buckets.map((bucket) => {
    const bucketDate = new Date(bucket.time);
    const bucketStart = Number.isNaN(bucketDate.getTime()) ? 0 : bucketDate.getTime();
    return {
      time: bucket.time,
      label: Number.isNaN(bucketDate.getTime()) ? bucket.time : formatRuntimeBucketLabel(bucketDate, bucketSize),
      consumed: Number(bucket.consumed || 0),
      refill: Number(bucket.refill || 0),
      refillEvents: Number(bucket.refill_events || 0),
      resetEvents: Number(bucket.reset_events || 0),
      pointCount: Number(bucket.point_count || 0),
      startValue: bucket.start_value ?? undefined,
      endValue: bucket.end_value ?? undefined,
      bucketStart,
      bucketEnd: bucketStart,
      anomaly: Boolean(bucket.anomaly),
      anomalyReasons: Array.isArray(bucket.anomaly_reasons) ? bucket.anomaly_reasons : [],
      coveragePct: bucket.coverage_pct,
      gapMinutes: bucket.gap_minutes,
    };
  });
  const peakBucket = rows.reduce<ConsumptionBucketRow | undefined>(
    (best, row) => (!best || row.consumed > best.consumed ? row : best),
    undefined,
  );
  const totalConsumed = Number(response.summary.total_consumed || 0);
  const avgPerHour = totalConsumed / Math.max(coverageHours || rangeHours, 1);
  const endValue = response.summary.end_value ?? undefined;
  const projectedEmptyHours = mode === 'tank_drawdown' && endValue != null && endValue > 0 && avgPerHour > 0
    ? endValue / avgPerHour
    : undefined;

  return {
    mode,
    rows,
    totalConsumed,
    refillVolume: Number(response.summary.total_refill || 0),
    refillEvents: Number(response.summary.refill_events || 0),
    resetEvents: Number(response.summary.reset_events || 0),
    startValue: response.summary.start_value ?? undefined,
    endValue,
    startTime: response.summary.coverage_start || undefined,
    endTime: response.summary.coverage_end || undefined,
    avgPerHour: Number(avgPerHour.toFixed(3)),
    projectedEmptyHours,
    pointCount: Number(response.summary.point_count || 0),
    requestedFrom,
    requestedTo,
    coverageStart,
    coverageEnd,
    coverageHours: Number(coverageHours.toFixed(2)),
    coverageRatio: Number(coverageRatio.toFixed(3)),
    isSampleCapped: false,
    hasPartialCoverage: !response.summary.is_complete,
    peakBucket,
    warnings: response.summary.warnings || [],
    anomalyCount: rows.filter((row) => row.anomaly).length,
  };
}

function makeRuntimeStateKey(label: string, index: number): string {
  const clean = label.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  return `state_${index}_${clean || 'runtime'}`;
}

function getRuntimeEditorStates(cfg: WidgetConfig): RuntimeCondition[] {
  if (cfg.runtime_states?.length) {
    return cfg.runtime_states.map((state, index) => ({
      id: state.id || `runtime-state-${index}`,
      label: state.label || `State ${index + 1}`,
      operator: state.operator || '>',
      value: state.value ?? '0',
      color: state.color || RUNTIME_STATE_COLORS[index % RUNTIME_STATE_COLORS.length],
    }));
  }

  return [{
    id: 'runtime-state-0',
    label: cfg.true_label || 'ON',
    operator: cfg.condition_operator || '>',
    value: cfg.condition_value ?? '0',
    color: cfg.true_color || '#30D158',
  }];
}

function getRuntimeStateModel(cfg: WidgetConfig): RuntimeStateModel {
  const conditions = getRuntimeEditorStates(cfg).map((state, index) => ({
    key: makeRuntimeStateKey(state.label, index),
    label: state.label || `State ${index + 1}`,
    color: state.color || RUNTIME_STATE_COLORS[index % RUNTIME_STATE_COLORS.length],
    operator: state.operator || '>',
    value: state.value ?? '0',
    kind: 'condition' as const,
  }));

  const fallback = {
    key: 'state_fallback',
    label: cfg.false_label || 'OFF',
    color: cfg.false_color || '#FF453A',
    kind: 'fallback' as const,
  };
  const unknown = {
    key: 'state_unknown',
    label: 'No telemetry',
    color: cfg.unknown_color || '#64748b',
    kind: 'unknown' as const,
  };
  const unmatchedBehavior = cfg.unmatched_runtime_behavior || 'fallback';

  return {
    conditions,
    fallback,
    unknown,
    unmatchedBehavior,
    allStates: [...conditions, ...(unmatchedBehavior === 'fallback' ? [fallback] : []), unknown],
  };
}

function runtimeConditionMatches(raw: number | string, condition: Pick<RuntimeResolvedState, 'operator' | 'value'>): boolean | null {
  const operator = condition.operator || '>';
  const target = condition.value ?? '0';
  const rawNumber = typeof raw === 'number' ? raw : Number(raw);
  const targetNumber = Number(target);
  const canCompareNumeric = Number.isFinite(rawNumber) && Number.isFinite(targetNumber);

  if (operator === 'contains') {
    return String(raw).toLowerCase().includes(String(target).toLowerCase());
  }

  if (operator === '==' || operator === '!=') {
    const matched = canCompareNumeric
      ? rawNumber === targetNumber
      : String(raw).trim().toLowerCase() === String(target).trim().toLowerCase();
    return operator === '==' ? matched : !matched;
  }

  if (!canCompareNumeric) return null;
  if (operator === '>') return rawNumber > targetNumber;
  if (operator === '>=') return rawNumber >= targetNumber;
  if (operator === '<') return rawNumber < targetNumber;
  if (operator === '<=') return rawNumber <= targetNumber;
  return null;
}

function evaluateRuntimeStateKey(
  point: { value?: number | string | null; state?: string | null },
  model: RuntimeStateModel,
): string {
  const raw = point.value ?? point.state;
  if (raw == null || raw === '') return model.unknown.key;

  for (const condition of model.conditions) {
    if (runtimeConditionMatches(raw, condition)) return condition.key;
  }

  return model.unmatchedBehavior === 'fallback' ? model.fallback.key : model.unknown.key;
}

function buildRuntimeHistogramRows(
  series: Array<{ time: string; device_id: string; value?: number | string | null; state?: string | null; duration_seconds?: number }>,
  ids: string[],
  cfg: WidgetConfig,
  model: RuntimeStateModel,
) {
  const rangeHours = cfg.range_hours || 168;
  const bucketSize = getRuntimeBucketSize(rangeHours, cfg.bucket_size);
  const to = Date.now();
  const from = to - rangeHours * 60 * 60 * 1000;
  const rows: Array<{
    time: string;
    label: string;
    [stateKey: string]: string | number;
    capacity: number;
    bucketStart: number;
    bucketEnd: number;
  }> = [];

  let cursor = floorRuntimeBucket(new Date(from), bucketSize);
  while (cursor.getTime() < to) {
    const next = addRuntimeBucket(cursor, bucketSize);
    const bucketStart = Math.max(cursor.getTime(), from);
    const bucketEnd = Math.min(next.getTime(), to);
    rows.push({
      time: cursor.toISOString(),
      label: formatRuntimeBucketLabel(cursor, bucketSize),
      ...Object.fromEntries(model.allStates.map((state) => [state.key, 0])),
      capacity: Number((((bucketEnd - bucketStart) / 36e5) * Math.max(ids.length, 1)).toFixed(2)),
      bucketStart,
      bucketEnd,
    });
    cursor = next;
  }

  const addDuration = (start: number, end: number, stateKey: string) => {
    if (end <= start) return;
    rows.forEach((row) => {
      const overlap = Math.min(end, row.bucketEnd) - Math.max(start, row.bucketStart);
      if (overlap > 0) row[stateKey] = Number(row[stateKey] || 0) + overlap / 36e5;
    });
  };

  ids.forEach((deviceId) => {
    const points = series
      .filter((p) => p.device_id === deviceId)
      .map((p) => ({ ...p, ts: new Date(p.time).getTime() }))
      .filter((p) => Number.isFinite(p.ts) && p.ts <= to)
      .sort((a, b) => a.ts - b.ts);

    if (!points.length) {
      addDuration(from, to, model.unknown.key);
      return;
    }

    let cursorTime = from;
    points.forEach((point, index) => {
      const durationMs = typeof point.duration_seconds === 'number' && Number.isFinite(point.duration_seconds)
        ? Math.max(point.duration_seconds * 1000, 0)
        : undefined;
      const rawStart = point.ts;
      const rawEnd = durationMs != null ? rawStart + durationMs : points[index + 1]?.ts ?? to;
      const segmentStart = clamp(Math.max(rawStart, cursorTime), from, to);
      const segmentEnd = clamp(rawEnd, from, to);

      if (segmentStart > cursorTime) addDuration(cursorTime, segmentStart, model.unknown.key);
      if (segmentEnd > segmentStart) {
        addDuration(segmentStart, segmentEnd, evaluateRuntimeStateKey(point, model));
        cursorTime = Math.max(cursorTime, segmentEnd);
      }
    });

    if (cursorTime < to) addDuration(cursorTime, to, model.unknown.key);
  });

  return rows.map((row) => ({
    ...row,
    ...Object.fromEntries(model.allStates.map((state) => [state.key, Number(Number(row[state.key] || 0).toFixed(2))])),
  }));
}

function formatCompactTime(value?: string): string {
  if (!value) return 'never';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'unknown';

  const diff = Date.now() - date.getTime();
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;

  if (diff < minute) return 'just now';
  if (diff < hour) return `${Math.max(1, Math.floor(diff / minute))}m ago`;
  if (diff < day) return `${Math.floor(diff / hour)}h ago`;
  if (diff < 7 * day) return `${Math.floor(diff / day)}d ago`;

  return date.toLocaleDateString([], { month: '2-digit', day: '2-digit' });
}

type GaugeColorBand = 'low' | 'midLow' | 'midHigh' | 'high';
const GAUGE_BANDS: { key: GaugeColorBand; label: string }[] = [
  { key: 'low', label: '0–25%' },
  { key: 'midLow', label: '25–50%' },
  { key: 'midHigh', label: '50–75%' },
  { key: 'high', label: '75–100%' },
];

function defaultGaugeColors(reverse?: boolean): { low: string; midLow: string; midHigh: string; high: string } {
  return reverse
    ? { low: '#2DD4A8', midLow: '#F5A623', midHigh: '#f97316', high: '#ef4444' }
    : { low: '#ef4444', midLow: '#f97316', midHigh: '#F5A623', high: '#2DD4A8' };
}

function resolveGaugeColors(cfg: WidgetConfig): { low: string; midLow: string; midHigh: string; high: string } {
  return { ...defaultGaugeColors(cfg.gauge_reverse), ...(cfg.gauge_colors || {}) };
}

function ensureMetricOption(metrics: DeviceMetric[], selected?: string): DeviceMetric[] {
  if (!selected || metrics.some((m) => m.key === selected)) return metrics;
  return [{ key: selected, numeric: true }, ...metrics];
}

function makeTimeSeriesKey(deviceId: string, metric: string): string {
  return `series_${deviceId}_${metric}`.replace(/[^a-zA-Z0-9_]/g, '_');
}

function getTimeSeriesMetricPairs(cfg: WidgetConfig, ids: string[]) {
  const metricMap = cfg.metric_by_device || {};
  const hasDeviceSpecificMetrics = Object.values(metricMap).some(Boolean);

  return ids
    .map((deviceId) => {
      const metric = hasDeviceSpecificMetrics ? metricMap[deviceId] : metricMap[deviceId] || cfg.metric || '';
      return {
        deviceId,
        metric,
        key: metric ? makeTimeSeriesKey(deviceId, metric) : '',
      };
    })
    .filter((item) => item.metric);
}

function hasTimeSeriesMetricBindings(cfg: WidgetConfig): boolean {
  const ids = cfg.device_ids || [];
  if (!ids.length) return false;
  if (cfg.chart_mode === 'runtime_histogram') return !!cfg.metric;
  const metricMap = cfg.metric_by_device || {};
  const hasDeviceSpecificMetrics = Object.values(metricMap).some(Boolean);
  if (hasDeviceSpecificMetrics) return ids.every((id) => !!metricMap[id]);
  return !!cfg.metric;
}

function getTimeSeriesStyleOption(mode?: WidgetConfig['chart_mode']) {
  return TIME_SERIES_STYLE_OPTIONS.find((option) => option.mode === (mode || 'line')) || TIME_SERIES_STYLE_OPTIONS[0];
}

type BuilderButtonIconName =
  | 'new-dashboard'
  | 'templates'
  | 'rename'
  | 'default'
  | 'delete'
  | 'save'
  | 'add-widget'
  | 'muted'
  | 'sound-on';

function BuilderButtonIcon({ icon }: { icon: BuilderButtonIconName }) {
  const common = {
    fill: 'none',
    stroke: 'currentColor',
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    strokeWidth: 2,
  };

  return (
    <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
      {icon === 'new-dashboard' && (
        <>
          <rect {...common} x="4" y="5" width="16" height="14" rx="2" />
          <path {...common} d="M8 9h5" />
          <path {...common} d="M8 13h3" />
          <path {...common} d="M16 11v6" />
          <path {...common} d="M13 14h6" />
        </>
      )}
      {icon === 'templates' && (
        <>
          <rect {...common} x="4" y="4" width="7" height="7" rx="1.5" />
          <rect {...common} x="13" y="4" width="7" height="7" rx="1.5" />
          <rect {...common} x="4" y="13" width="7" height="7" rx="1.5" />
          <path {...common} d="M14 16h5" />
          <path {...common} d="M16.5 13.5v5" />
        </>
      )}
      {icon === 'rename' && (
        <>
          <path {...common} d="M4 20h4l10-10a2.8 2.8 0 0 0-4-4L4 16z" />
          <path {...common} d="M13 7l4 4" />
        </>
      )}
      {icon === 'default' && (
        <>
          <path {...common} d="M12 3l2.7 5.5 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.8 1-6.1-4.4-4.3 6.1-.9z" />
        </>
      )}
      {icon === 'delete' && (
        <>
          <path {...common} d="M5 7h14" />
          <path {...common} d="M10 11v6" />
          <path {...common} d="M14 11v6" />
          <path {...common} d="M8 7l1-3h6l1 3" />
          <path {...common} d="M7 7l1 13h8l1-13" />
        </>
      )}
      {icon === 'save' && (
        <>
          <path {...common} d="M5 4h12l2 2v14H5z" />
          <path {...common} d="M8 4v6h8V4" />
          <path {...common} d="M8 20v-6h8v6" />
        </>
      )}
      {icon === 'add-widget' && (
        <>
          <rect {...common} x="4" y="4" width="6" height="6" rx="1.5" />
          <rect {...common} x="14" y="4" width="6" height="6" rx="1.5" />
          <rect {...common} x="4" y="14" width="6" height="6" rx="1.5" />
          <path {...common} d="M17 14v6" />
          <path {...common} d="M14 17h6" />
        </>
      )}
      {icon === 'muted' && (
        <>
          <path {...common} d="M9 18a3 3 0 0 0 6 0" />
          <path {...common} d="M6 8a6 6 0 0 1 12 0c0 5 2 6 2 6H6s2-1 2-6" />
          <path {...common} d="M4 4l16 16" />
        </>
      )}
      {icon === 'sound-on' && (
        <>
          <path {...common} d="M9 18a3 3 0 0 0 6 0" />
          <path {...common} d="M6 8a6 6 0 0 1 12 0c0 5 2 6 2 6H4s2-1 2-6" />
          <path {...common} d="M4 4l2 2" />
          <path {...common} d="M18 4l2 2" />
        </>
      )}
    </svg>
  );
}

interface DashboardsPanelProps {
  user: User;
  readOnly?: boolean;
  variant?: 'builder' | 'overview';
  selectedDashboardId?: string;
  onDashboardSelect?: (dashboardId: string) => void;
  tenantPlan?: TenantPlan | null;
}

export default function DashboardsPanel({
  user,
  readOnly: readOnlyProp,
  variant = 'builder',
  selectedDashboardId,
  onDashboardSelect,
  tenantPlan,
}: DashboardsPanelProps) {
  const isOverviewEmbed = variant === 'overview';
  const readOnly = isOverviewEmbed ? true : readOnlyProp ?? user.role !== 'admin';
  const isMobile = useIsMobile();

  const [dashboards, setDashboards] = useState<DashboardSummary[]>([]);
  const [activeId, setActiveId] = useState('');
  const [activeName, setActiveName] = useState('');
  const [widgets, setWidgets] = useState<DashboardWidget[]>([]);
  const [dirty, setDirty] = useState(false);
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [editorWidget, setEditorWidget] = useState<DashboardWidget | null>(null);
  const [widgetAlarms, setWidgetAlarms] = useState<WidgetAlarm[]>([]);
  const [soundEnabled, setSoundEnabled] = useState(false);
  const playAlarm = useAlarmAudio(soundEnabled);
  const lastBeepRef = useRef(0);

  async function refreshAlarms() {
    if (!activeId) return;
    try {
      await evaluateWidgetAlarms(activeId);
      const alarms = await getWidgetAlarms(activeId);
      setWidgetAlarms(alarms);
    } catch (err: any) {
      console.error('Failed to refresh widget alarms:', err.response?.data?.error || err.message);
    }
  }

  async function handleAcknowledge(alarmId: string) {
    try {
      await acknowledgeWidgetAlarm(alarmId);
      await refreshAlarms();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to acknowledge alarm');
    }
  }

  function chooseDashboard(dashboardId: string) {
    setActiveId(dashboardId);
    onDashboardSelect?.(dashboardId);
  }

  async function refreshList(selectId?: string) {
    const list: DashboardSummary[] = await getDashboards();
    setDashboards(list);
    const wanted = selectId || selectedDashboardId || activeId;
    const next = wanted && list.some((d) => d.id === wanted)
      ? wanted
      : (list.find((d) => d.is_default) || list[0])?.id || '';
    setActiveId(next);
    onDashboardSelect?.(next);
  }

  useEffect(() => {
    refreshList()
      .catch((err) => console.error('Failed to load dashboards:', err.response?.data?.error || err.message))
      .finally(() => setLoading(false));
    getDevices().then(setDevices).catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedDashboardId && selectedDashboardId !== activeId) {
      setActiveId(selectedDashboardId);
    }
  }, [selectedDashboardId, activeId]);

  useEffect(() => {
    if (!activeId) {
      setWidgets([]);
      setActiveName('');
      setWidgetAlarms([]);
      return;
    }
    getDashboard(activeId)
      .then((d) => {
        setActiveName(d.name || '');
        setWidgets(d.layout?.widgets || []);
        setDirty(false);
      })
      .catch((err) => console.error('Failed to load dashboard:', err.response?.data?.error || err.message));
    refreshAlarms();
    const interval = setInterval(refreshAlarms, 30000);
    return () => clearInterval(interval);
  }, [activeId]);

  // Beep periodically while unacknowledged active alarms exist and sound is enabled.
  useEffect(() => {
    if (!soundEnabled) return;
    const activeUnacked = widgetAlarms.filter((a) => a.status === 'active');
    if (activeUnacked.length === 0) return;
    const now = Date.now();
    if (now - lastBeepRef.current < 5000) return;
    lastBeepRef.current = now;
    playAlarm();
    const id = setInterval(() => {
      const stillActive = widgetAlarms.some((a) => a.status === 'active');
      if (stillActive && soundEnabled) playAlarm();
      else clearInterval(id);
    }, 5000);
    return () => clearInterval(id);
  }, [soundEnabled, widgetAlarms, playAlarm]);

  const activeDashboard = useMemo(
    () => dashboards.find((d) => d.id === activeId) || null,
    [dashboards, activeId]
  );
  const activeAlarmCount = widgetAlarms.filter((alarm) => alarm.status === 'active').length;
  const lastUpdatedLabel = activeDashboard?.updated_at
    ? new Date(activeDashboard.updated_at).toLocaleString([], {
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
    : 'Not saved yet';
  const dashboardLimit = tenantPlan?.max_dashboards ?? null;
  const dashboardLimitReached = dashboardLimit != null && dashboards.length >= dashboardLimit;
  const dashboardsRemaining = dashboardLimit == null ? null : Math.max(dashboardLimit - dashboards.length, 0);
  const dashboardUsageLabel = dashboardLimit == null ? String(dashboards.length) : `${dashboards.length}/${dashboardLimit}`;
  const dashboardLimitCopy = dashboardLimit == null
    ? 'Unlimited dashboards on this plan'
    : dashboardLimitReached
      ? 'Plan dashboard limit reached'
      : `${dashboardsRemaining} dashboard${dashboardsRemaining === 1 ? '' : 's'} remaining`;
  const createDashboardTitle = dashboardLimitReached
    ? `This plan allows ${dashboardLimit} dashboard${dashboardLimit === 1 ? '' : 's'}.`
    : 'Create dashboard';

  async function handleNewDashboard() {
    if (dashboardLimitReached) {
      alert(`Dashboard limit reached for this plan. Maximum dashboards: ${dashboardLimit}.`);
      return;
    }
    const name = window.prompt('Dashboard name', 'New Dashboard');
    if (!name) return;
    try {
      const d = await createDashboard({ name });
      await refreshList(d.id);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to create dashboard');
    }
  }

  async function handleRename() {
    if (!activeId) return;
    const name = window.prompt('Dashboard name', activeName);
    if (!name || name === activeName) return;
    try {
      await updateDashboard(activeId, { name });
      setActiveName(name);
      await refreshList(activeId);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to rename dashboard');
    }
  }

  async function handleSetDefault() {
    if (!activeId) return;
    try {
      await updateDashboard(activeId, { is_default: true });
      await refreshList(activeId);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to set default dashboard');
    }
  }

  async function handleDelete() {
    if (!activeId) return;
    if (!window.confirm(`Delete dashboard "${activeName}"? This cannot be undone.`)) return;
    try {
      await deleteDashboard(activeId);
      await refreshList('');
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to delete dashboard');
    }
  }

  async function handleSaveLayout() {
    if (!activeId) return;
    setSaving(true);
    try {
      await updateDashboard(activeId, { layout: { widgets } });
      setDirty(false);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to save layout');
    } finally {
      setSaving(false);
    }
  }

  function handleAddWidget() {
    const maxY = widgets.reduce((acc, w) => Math.max(acc, w.y + w.h), 0);
    setEditorWidget({
      id: `w-${Date.now()}`,
      type: 'kpi',
      x: 0,
      y: maxY,
      w: 4,
      h: 3,
      config: { title: '', device_ids: [], aggregation: 'avg', range_hours: 24 },
    });
  }

  function handleSaveWidget(updated: DashboardWidget) {
    setWidgets((prev) => {
      const exists = prev.some((w) => w.id === updated.id);
      return exists ? prev.map((w) => (w.id === updated.id ? updated : w)) : [...prev, updated];
    });
    setDirty(true);
    setEditorWidget(null);
  }

  function handleRemoveWidget(id: string) {
    setWidgets((prev) => prev.filter((w) => w.id !== id));
    setDirty(true);
  }

  function handleLayoutStop(layout: ReactGridLayout.Layout[]) {
    setWidgets((prev) =>
      prev.map((w) => {
        const item = layout.find((l) => l.i === w.id);
        return item ? { ...w, x: item.x, y: item.y, w: item.w, h: item.h } : w;
      })
    );
    setDirty(true);
  }

  async function handlePickTemplate(templateId: string, name: string) {
    if (dashboardLimitReached) {
      alert(`Dashboard limit reached for this plan. Maximum dashboards: ${dashboardLimit}.`);
      return;
    }
    try {
      const d = await createDashboard({ name, from_template_id: templateId });
      setGalleryOpen(false);
      await refreshList(d.id);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to create dashboard from template');
    }
  }

  async function handleSaveAsTemplate(name: string, industry: string, description: string) {
    await createDashboardTemplate({ name, industry, description, layout: { widgets } });
  }

  const layoutItems = widgets.map((w) => ({
    i: w.id,
    x: clamp(w.x, 0, 11),
    y: Math.max(0, w.y),
    w: clamp(w.w, 1, 12),
    h: Math.max(1, w.h),
    minW: 2,
    minH: 2,
  }));
  const mobileWidgets = [...widgets].sort((a, b) => (a.y - b.y) || (a.x - b.x));
  const getMobileWidgetHeight = (w: DashboardWidget) => {
    if (w.type === 'gauge') return Math.max(360, w.h * 104);
    if (w.type === 'timeseries') return w.config.chart_mode === 'runtime_histogram'
      ? Math.max(380, w.h * 106)
      : Math.max(360, w.h * 104);
    if (w.type === 'consumption') return Math.max(380, w.h * 106);
    if (w.type === 'tank') return Math.max(340, w.h * 100);
    if (w.type === 'gps_map') return Math.max(360, w.h * 104);
    if (w.type === 'status' || w.type === 'alerts') return Math.max(340, w.h * 100);
    return Math.max(260, w.h * 92);
  };

  if (loading) {
    return (
      <section className={`sd-builder-page ${isOverviewEmbed ? 'is-overview-embed' : ''}`}>
        <article className="sd-panel sd-builder-loading">Loading dashboards...</article>
      </section>
    );
  }

  return (
    <section className={`sd-builder-page ${isOverviewEmbed ? 'is-overview-embed' : ''}`}>
      {!isOverviewEmbed && (
        <>
          <article className="sd-panel sd-builder-toolbar">
            <div className="sd-builder-toolbar-main">
              <label className="sd-builder-select-label">
                <span>Dashboard</span>
                <select
                  className="sd-builder-select"
                  value={activeId}
                  onChange={(e) => chooseDashboard(e.target.value)}
                  aria-label="Select dashboard"
                >
                  {dashboards.length === 0 && <option value="">No dashboards</option>}
                  {dashboards.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.is_default ? 'Default - ' : ''}{d.name} ({d.widget_count})
                    </option>
                  ))}
                </select>
              </label>
              <div className="sd-builder-state-strip">
                {activeDashboard?.is_default && <span className="sd-builder-badge is-default">Default</span>}
                {dirty && <span className="sd-builder-badge is-dirty">Unsaved changes</span>}
                <span className={`sd-builder-badge ${readOnly ? 'is-readonly' : 'is-editable'}`}>
                  {readOnly ? 'Read only' : 'Edit mode'}
                </span>
                <span className={`sd-builder-badge ${dashboardLimitReached ? 'is-limit' : 'is-readonly'}`}>
                  {dashboardLimit == null ? 'Unlimited dashboards' : `${dashboardUsageLabel} dashboards`}
                </span>
              </div>
            </div>

            {!readOnly && (
              <div className="sd-builder-actions">
                <button
                  className="sd-builder-button is-create"
                  disabled={dashboardLimitReached}
                  onClick={handleNewDashboard}
                  title={createDashboardTitle}
                  type="button"
                >
                  <BuilderButtonIcon icon="new-dashboard" />
                  <span>New Dashboard</span>
                </button>
                <button
                  className="sd-builder-button is-template"
                  onClick={() => setGalleryOpen(true)}
                  title="Open dashboard templates"
                  type="button"
                >
                  <BuilderButtonIcon icon="templates" />
                  <span>Templates</span>
                </button>
                {activeId && (
                  <>
                    <button className="sd-builder-button is-edit" onClick={handleRename} type="button">
                      <BuilderButtonIcon icon="rename" />
                      <span>Rename</span>
                    </button>
                    <button
                      className={`sd-builder-button is-default-action ${activeDashboard?.is_default ? 'is-disabled' : ''}`}
                      onClick={handleSetDefault}
                      disabled={!!activeDashboard?.is_default}
                      title="Set as default dashboard"
                      type="button"
                    >
                      <BuilderButtonIcon icon="default" />
                      <span>Set Default</span>
                    </button>
                    <button className="sd-builder-button is-danger" onClick={handleDelete} type="button">
                      <BuilderButtonIcon icon="delete" />
                      <span>Delete</span>
                    </button>
                    <button
                      className="sd-builder-button is-save"
                      onClick={handleSaveLayout}
                      disabled={!dirty || saving}
                      type="button"
                    >
                      <BuilderButtonIcon icon="save" />
                      <span>{saving ? 'Saving...' : 'Save Layout'}</span>
                    </button>
                    <button className="sd-builder-button is-primary is-add" onClick={handleAddWidget} type="button">
                      <BuilderButtonIcon icon="add-widget" />
                      <span>Add Widget</span>
                    </button>
                  </>
                )}
              </div>
            )}

            {activeId && (
              <button
                className={`sd-builder-sound-button ${soundEnabled ? 'is-on' : ''}`}
                onClick={() => setSoundEnabled((s) => !s)}
                title={soundEnabled ? 'Alarm sounds enabled' : 'Alarm sounds muted'}
                type="button"
              >
                <BuilderButtonIcon icon={soundEnabled ? 'sound-on' : 'muted'} />
                <span>{soundEnabled ? 'Sound On' : 'Muted'}</span>
              </button>
            )}
          </article>

          <section className="sd-builder-stat-grid" aria-label="Dashboard builder summary">
            <article className={`sd-builder-stat ${dashboardLimitReached ? 'is-danger' : ''}`}>
              <span>Dashboards</span>
              <strong>{dashboardUsageLabel}</strong>
              <p>{dashboardLimitCopy}</p>
            </article>
            <article className="sd-builder-stat is-blue">
              <span>Widgets</span>
              <strong>{widgets.length}</strong>
              <p>{widgets.length === 1 ? 'Panel configured' : 'Panels configured'}</p>
            </article>
            <article className={`sd-builder-stat ${activeAlarmCount > 0 ? 'is-danger' : 'is-success'}`}>
              <span>Widget Alarms</span>
              <strong>{activeAlarmCount}</strong>
              <p>{activeAlarmCount > 0 ? 'Requires acknowledgement' : 'No active widget alarms'}</p>
            </article>
            <article className="sd-builder-stat is-muted">
              <span>Updated</span>
              <strong>{lastUpdatedLabel}</strong>
              <p>{readOnly ? 'Viewer session' : 'Admin builder session'}</p>
            </article>
          </section>
        </>
      )}

      {isOverviewEmbed && activeId && (
        <article className="sd-panel sd-builder-overview-head">
          <div>
            <span>SELECTED DASHBOARD</span>
            <h2>{activeName || activeDashboard?.name || 'Default Dashboard'}</h2>
            <p>{widgets.length} widget{widgets.length === 1 ? '' : 's'} streaming from the selected tenant layout.</p>
          </div>
          <div className="sd-builder-state-strip">
            <span className="sd-builder-badge is-default">Overview</span>
            <span className={`sd-builder-badge ${activeAlarmCount > 0 ? 'is-alarm' : 'is-readonly'}`}>
              {activeAlarmCount > 0 ? `${activeAlarmCount} active alarm${activeAlarmCount === 1 ? '' : 's'}` : 'No widget alarms'}
            </span>
            <span className="sd-builder-badge is-readonly">{lastUpdatedLabel}</span>
          </div>
        </article>
      )}

      {!activeId ? (
        <article className="sd-panel sd-builder-empty">
          <div className="sd-builder-empty-mark">DB</div>
          <h3>{isOverviewEmbed ? 'No default dashboard available' : 'No dashboards yet'}</h3>
          <p>{isOverviewEmbed ? 'Create or set a default dashboard in Dashboard Builder.' : dashboardLimitReached ? dashboardLimitCopy : 'Create a blank workspace or start from an industry template pack.'}</p>
          {!readOnly && (
            <div className="sd-builder-empty-actions">
              <button
                className="sd-builder-button is-create"
                disabled={dashboardLimitReached}
                onClick={handleNewDashboard}
                title={createDashboardTitle}
                type="button"
              >
                <BuilderButtonIcon icon="new-dashboard" />
                <span>New Dashboard</span>
              </button>
              <button
                className="sd-builder-button is-template"
                onClick={() => setGalleryOpen(true)}
                title="Open dashboard templates"
                type="button"
              >
                <BuilderButtonIcon icon="templates" />
                <span>Templates</span>
              </button>
            </div>
          )}
        </article>
      ) : widgets.length === 0 ? (
        <article className="sd-panel sd-builder-empty">
          <div className="sd-builder-empty-mark">00</div>
          <h3>{isOverviewEmbed ? 'Default dashboard is empty' : 'This dashboard is empty'}</h3>
          <p>{readOnly ? 'No widgets have been configured yet.' : 'Add your first widget to start building.'}</p>
          {!readOnly && (
            <button className="sd-builder-button is-primary is-add" onClick={handleAddWidget} type="button">
              <BuilderButtonIcon icon="add-widget" />
              <span>Add Widget</span>
            </button>
          )}
        </article>
      ) : isMobile ? (
        <div className="sd-builder-mobile-stack">
          {mobileWidgets.map((w) => (
            <div className="sd-builder-mobile-widget" key={w.id} style={{ height: getMobileWidgetHeight(w) }}>
              <WidgetFrame
                widget={w}
                readOnly={readOnly}
                devices={devices}
                widgetAlarms={widgetAlarms}
                onEdit={setEditorWidget}
                onRemove={handleRemoveWidget}
                onAcknowledge={handleAcknowledge}
              />
            </div>
          ))}
        </div>
      ) : (
        <article className="sd-panel sd-builder-canvas">
          <GridLayout
            className="layout sd-builder-grid"
            layout={layoutItems}
            cols={12}
            rowHeight={90}
            margin={[12, 12]}
            isDraggable={!readOnly}
            isResizable={!readOnly}
            draggableHandle=".widget-drag-handle"
            onDragStop={handleLayoutStop}
            onResizeStop={handleLayoutStop}
          >
            {widgets.map((w) => (
              <div key={w.id}>
                <WidgetFrame
                  widget={w}
                  readOnly={readOnly}
                  devices={devices}
                  widgetAlarms={widgetAlarms}
                  onEdit={setEditorWidget}
                  onRemove={handleRemoveWidget}
                  onAcknowledge={handleAcknowledge}
                />
              </div>
            ))}
          </GridLayout>
        </article>
      )}

      {galleryOpen && (
        <TemplateGalleryModal
          canCreateDashboard={!dashboardLimitReached}
          createDisabledReason={createDashboardTitle}
          hasCurrent={!!activeId && widgets.length > 0}
          onClose={() => setGalleryOpen(false)}
          onPick={handlePickTemplate}
          onSaveCurrent={handleSaveAsTemplate}
        />
      )}

      {editorWidget && (
        <WidgetConfigModal
          widget={editorWidget}
          devices={devices}
          onSave={handleSaveWidget}
          onClose={() => setEditorWidget(null)}
        />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Widget frame + per-type content
// ---------------------------------------------------------------------------

interface WidgetFrameProps {
  widget: DashboardWidget;
  readOnly: boolean;
  devices: Device[];
  widgetAlarms: WidgetAlarm[];
  onEdit: (w: DashboardWidget) => void;
  onRemove: (id: string) => void;
  onAcknowledge?: (alarmId: string) => void;
}

function WidgetFrame({ widget, readOnly, devices, widgetAlarms, onEdit, onRemove, onAcknowledge }: WidgetFrameProps) {
  const displayTypeLabel = widget.type === 'timeseries'
    ? getTimeSeriesStyleOption(widget.config.chart_mode).label
    : TYPE_LABELS[widget.type];
  const title = widget.config.title || displayTypeLabel;
  const alarm = widget.type === 'gauge' ? widgetAlarms.find((a) => a.widget_id === widget.id && a.status === 'active') : undefined;
  const cfg = widget.config;
  const deviceIds = cfg.device_ids || [];
  const sourceLabel = getWidgetSourceLabel(widget, devices);
  const metricLabel = getWidgetMetricLabel(widget);
  const windowLabel = getWidgetWindowLabel(widget);
  const isConfigured =
    widget.type === 'alerts' ||
    widget.type === 'text' ||
    (widget.type === 'gps_map'
      ? deviceIds.length > 0
      : widget.type === 'status'
        ? deviceIds.length > 0
        : widget.type === 'timeseries'
          ? hasTimeSeriesMetricBindings(cfg)
          : !!cfg.metric && deviceIds.length > 0);
  return (
    <article className={`sd-builder-widget sd-builder-widget-type-${widget.type} ${alarm ? 'has-active-alarm' : ''} ${readOnly ? 'is-readonly' : ''}`}>
      <div className="sd-builder-widget-header">
        {!readOnly && (
          <span className="widget-drag-handle sd-builder-drag-handle" title="Drag to move">
            <span />
            <span />
            <span />
          </span>
        )}
        <span className="sd-builder-widget-icon" aria-hidden="true">
          <WidgetTypeIcon type={widget.type} />
        </span>
        <div className="sd-builder-widget-title">
          <span>{displayTypeLabel}</span>
          <strong>{title}</strong>
        </div>
        <span className={`sd-builder-live-badge ${isConfigured ? 'is-bound' : 'is-unbound'}`}>
          <span />
          {isConfigured ? 'Live' : 'Unbound'}
        </span>
        {alarm && (
          <span
            className={`sd-builder-alarm-badge ${alarm.threshold_type === 'crit' ? 'is-critical' : 'is-warning'}`}
            title={`${alarm.metric} ${alarm.threshold_type} threshold ${alarm.direction} ${alarm.threshold_value} (value ${alarm.value_at_alarm})`}
          >
            {alarm.threshold_type.toUpperCase()}
          </span>
        )}
        {!readOnly && (
          <span className="sd-builder-widget-actions">
            <button className="sd-builder-widget-action" onClick={() => onEdit(widget)} title="Edit widget" aria-label="Edit widget" type="button">
              <WidgetActionIcon action="edit" />
            </button>
            <button className="sd-builder-widget-action is-danger" onClick={() => onRemove(widget.id)} title="Remove widget" aria-label="Remove widget" type="button">
              <WidgetActionIcon action="remove" />
            </button>
          </span>
        )}
      </div>
      <div className="sd-builder-widget-body">
        <WidgetContent widget={widget} devices={devices} readOnly={readOnly} widgetAlarms={widgetAlarms} onAcknowledge={onAcknowledge} />
      </div>
      <div className="sd-builder-widget-footer">
        <span title={metricLabel}>
          <b>Metric</b>
          {metricLabel}
        </span>
        <span title={sourceLabel}>
          <b>Source</b>
          {sourceLabel}
        </span>
        <span title={windowLabel}>
          <b>Window</b>
          {windowLabel}
        </span>
      </div>
    </article>
  );
}

function getWidgetSourceLabel(widget: DashboardWidget, devices: Device[]) {
  const ids = widget.config.device_ids || [];
  if (widget.type === 'alerts') return 'Tenant alerts';
  if (widget.type === 'text') return 'Operator note';
  if (ids.length === 0) return 'No source bound';
  if (ids.length === 1) return devices.find((device) => device.id === ids[0])?.name || '1 asset';
  return `${ids.length} assets`;
}

function getWidgetMetricLabel(widget: DashboardWidget) {
  const cfg = widget.config;
  if (widget.type === 'alerts') return 'Alert stream';
  if (widget.type === 'status') return 'Device status';
  if (widget.type === 'text') return 'Text block';
  if (widget.type === 'gps_map') return cfg.location_metric || `${cfg.latitude_metric || 'latitude'} / ${cfg.longitude_metric || 'longitude'}`;
  if (widget.type === 'consumption') {
    return cfg.metric ? `${cfg.metric} (${getConsumptionModeLabel(cfg.consumption_mode)})` : 'No metric bound';
  }
  if (widget.type === 'timeseries') {
    if (cfg.chart_mode === 'runtime_histogram') {
      const stateCount = getRuntimeStateModel(cfg).conditions.length;
      return cfg.metric ? `${cfg.metric} runtime (${stateCount} rule${stateCount === 1 ? '' : 's'})` : 'No metric bound';
    }
    const ids = cfg.device_ids || [];
    const pairs = getTimeSeriesMetricPairs(cfg, ids);
    const uniqueMetrics = Array.from(new Set(pairs.map((pair) => pair.metric)));
    if (!uniqueMetrics.length) return 'No metric bound';
    if (uniqueMetrics.length === 1) return uniqueMetrics[0];
    return `${uniqueMetrics.length} metrics`;
  }
  return cfg.metric || 'No metric bound';
}

function getWidgetWindowLabel(widget: DashboardWidget) {
  if (widget.type === 'alerts' || widget.type === 'status') return 'Live';
  if (widget.type === 'text') return 'Static';
  const hours = widget.config.range_hours;
  if (!hours) return 'Latest';
  if (hours < 24) return `${hours}h`;
  if (hours % 24 === 0) return `${hours / 24}d`;
  return `${hours}h`;
}

function WidgetTypeIcon({ type }: { type: DashboardWidgetType }) {
  const common = {
    fill: 'none',
    stroke: 'currentColor',
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    strokeWidth: 2,
  };

  return (
    <svg viewBox="0 0 24 24" focusable="false">
      {type === 'kpi' && (
        <>
          <path {...common} d="M5 17V7" />
          <path {...common} d="M12 17V4" />
          <path {...common} d="M19 17v-8" />
          <path {...common} d="M4 20h16" />
        </>
      )}
      {type === 'timeseries' && (
        <>
          <path {...common} d="M4 18h16" />
          <path {...common} d="M5 15l4-5 4 3 5-7" />
          <path {...common} d="M5 6v12" />
        </>
      )}
      {type === 'consumption' && (
        <>
          <path {...common} d="M7 4h10l1 4v11H6V8z" />
          <path {...common} d="M8 15h8" />
          <path {...common} d="M10 8h4" />
          <path {...common} d="M11 20c0-3 4-3 4-6" />
        </>
      )}
      {type === 'gauge' && (
        <>
          <path {...common} d="M4 14a8 8 0 0 1 16 0" />
          <path {...common} d="M12 14l4-5" />
          <path {...common} d="M8 18h8" />
        </>
      )}
      {type === 'tank' && (
        <>
          <path {...common} d="M8 4h8l1 3v12H7V7z" />
          <path {...common} d="M7 14h10" />
          <path {...common} d="M10 7h4" />
        </>
      )}
      {type === 'gps_map' && (
        <>
          <path {...common} d="M12 21s6-5.4 6-11a6 6 0 0 0-12 0c0 5.6 6 11 6 11z" />
          <circle {...common} cx="12" cy="10" r="2" />
        </>
      )}
      {type === 'state' && (
        <>
          <path {...common} d="M6 8h7" />
          <path {...common} d="M11 16h7" />
          <circle {...common} cx="17" cy="8" r="3" />
          <circle {...common} cx="7" cy="16" r="3" />
        </>
      )}
      {type === 'alerts' && (
        <>
          <path {...common} d="M12 4l9 16H3z" />
          <path {...common} d="M12 9v4" />
          <path {...common} d="M12 17h.01" />
        </>
      )}
      {type === 'status' && (
        <>
          <rect {...common} x="4" y="5" width="16" height="14" rx="2" />
          <path {...common} d="M8 10h8" />
          <path {...common} d="M8 14h5" />
        </>
      )}
      {type === 'text' && (
        <>
          <path {...common} d="M5 6h14" />
          <path {...common} d="M8 6v12" />
          <path {...common} d="M5 18h6" />
        </>
      )}
    </svg>
  );
}

function WidgetActionIcon({ action }: { action: 'edit' | 'remove' }) {
  const common = {
    fill: 'none',
    stroke: 'currentColor',
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    strokeWidth: 2,
  };

  return (
    <svg viewBox="0 0 24 24" focusable="false">
      {action === 'edit' ? (
        <>
          <path {...common} d="M4 20h4l10-10a2.8 2.8 0 0 0-4-4L4 16z" />
          <path {...common} d="M13 7l4 4" />
        </>
      ) : (
        <>
          <path {...common} d="M5 7h14" />
          <path {...common} d="M10 11v6" />
          <path {...common} d="M14 11v6" />
          <path {...common} d="M8 7l1-3h6l1 3" />
          <path {...common} d="M7 7l1 13h8l1-13" />
        </>
      )}
    </svg>
  );
}

function WidgetContent({ widget, devices, readOnly, widgetAlarms, onAcknowledge }: { widget: DashboardWidget; devices: Device[]; readOnly: boolean; widgetAlarms: WidgetAlarm[]; onAcknowledge?: (alarmId: string) => void }) {
  const needsMetric =
    widget.type === 'timeseries'
      ? !hasTimeSeriesMetricBindings(widget.config)
      : METRIC_TYPES.includes(widget.type) &&
        (!(widget.config.device_ids || []).length || !widget.config.metric);
  const needsDevices = widget.type === 'gps_map' && !(widget.config.device_ids || []).length;
  const statusUnbound = widget.type === 'status' && !(widget.config.device_ids || []).length;

  if (needsMetric || needsDevices || statusUnbound) {
    return (
      <div style={styles.placeholder}>
        {readOnly ? 'No devices bound to this widget.' : 'Edit widget to bind devices'}
      </div>
    );
  }

  switch (widget.type) {
    case 'kpi':
    case 'timeseries':
    case 'gauge':
    case 'tank':
    case 'state':
      return <MetricWidget widget={widget} devices={devices} widgetAlarms={widgetAlarms} onAcknowledge={onAcknowledge} />;
    case 'consumption':
      return <ConsumptionWidget widget={widget} devices={devices} />;
    case 'gps_map':
      return <GpsMapWidget widget={widget} devices={devices} />;
    case 'alerts':
      return <AlertsWidget />;
    case 'status':
      return <StatusWidget widget={widget} />;
    case 'text':
      return (
        <div style={styles.textWidget}>{widget.config.text || 'Empty text block'}</div>
      );
    default:
      return null;
  }
}

function MetricWidget({ widget, devices, widgetAlarms, onAcknowledge }: { widget: DashboardWidget; devices: Device[]; widgetAlarms: WidgetAlarm[]; onAcknowledge?: (alarmId: string) => void }) {
  const cfg = widget.config;
  const ids = cfg.device_ids || [];
  const [data, setData] = useState<WidgetDataResponse | null>(null);
  const [error, setError] = useState('');
  const configKey = JSON.stringify(cfg);
  const activeAlarm = widget.type === 'gauge' ? widgetAlarms.find((a) => a.widget_id === widget.id && a.status === 'active') : undefined;

  useEffect(() => {
    if (!ids.length) return;
    if (widget.type !== 'timeseries' && !cfg.metric) return;
    let cancelled = false;
    const load = () => {
      if (widget.type === 'timeseries') {
        if (cfg.chart_mode === 'runtime_histogram') {
          if (!cfg.metric) {
            setData(null);
            return;
          }
          getWidgetData({
            device_ids: ids,
            metric: cfg.metric,
            range_hours: cfg.range_hours || 168,
            bucket_size: getRuntimeBucketSize(cfg.range_hours || 168, cfg.bucket_size),
            aggregation: 'runtime',
          })
            .then((d: WidgetDataResponse) => {
              if (!cancelled) {
                setData(d);
                setError('');
              }
            })
            .catch((err: any) => {
              if (!cancelled) setError(err.response?.data?.error || 'Failed to load data');
            });
          return;
        }

        const pairs = getTimeSeriesMetricPairs(cfg, ids);
        if (!pairs.length) {
          setData(null);
          return;
        }
        Promise.all(
          pairs.map((pair) => (
            getWidgetData({
              device_ids: [pair.deviceId],
              metric: pair.metric,
              range_hours: cfg.range_hours || 24,
              bucket_size: getTrendBucketSize(cfg.range_hours || 24),
              aggregation: cfg.aggregation || 'avg',
            }).then((response: WidgetDataResponse) => ({ pair, response }))
          )),
        )
          .then((responses) => {
            if (cancelled) return;
            const series = responses.flatMap(({ pair, response }) => (
              (response.series || []).map((point) => ({
                ...point,
                device_id: pair.deviceId,
                metric: pair.metric,
                series_key: pair.key,
              }))
            ));
            const latest = responses.flatMap(({ pair, response }) => (
              (response.latest || []).map((point) => ({
                ...point,
                device_id: pair.deviceId,
                metric: pair.metric,
              }))
            ));
            setData({ series, latest } as WidgetDataResponse);
            setError('');
          })
          .catch((err: any) => {
            if (!cancelled) setError(err.response?.data?.error || 'Failed to load data');
          });
        return;
      }

      getWidgetData({
        device_ids: ids,
        metric: cfg.metric!,
        range_hours: cfg.range_hours || 24,
        bucket_size: getTrendBucketSize(cfg.range_hours || 24),
        aggregation: cfg.aggregation || 'avg',
      })
        .then((d: WidgetDataResponse) => {
          if (!cancelled) {
            setData(d);
            setError('');
          }
        })
        .catch((err: any) => {
          if (!cancelled) setError(err.response?.data?.error || 'Failed to load data');
        });
    };
    load();
    const interval = setInterval(load, 30000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [configKey]);

  if (error) return <div style={styles.widgetError}>{error}</div>;
  if (!data) return <div style={styles.placeholder}>Loading...</div>;

  const deviceName = (id: string) => devices.find((d) => d.id === id)?.name || id;

  if (widget.type === 'kpi') {
    const latest = data.latest.find((l) => l.device_id === ids[0]) || data.latest[0];
    const value = typeof latest?.value === 'number' ? latest.value : null;
    const freshness = formatLatestFreshness(latest);
    // Threshold semantics: lower-is-worse — a value below warn/crit trips the color
    // (e.g. fuel level, battery). Higher-is-worse metrics should leave thresholds unset.
    let color = '#2DD4A8';
    if (value != null && cfg.thresholds?.crit != null && value < cfg.thresholds.crit) color = '#ef4444';
    else if (value != null && cfg.thresholds?.warn != null && value < cfg.thresholds.warn) color = '#F5A623';
    return (
      <div className="sd-builder-kpi-content" style={styles.kpiWrap}>
        <div style={{ ...styles.kpiValue, color }}>
          {formatValue(latest?.value)}
          {cfg.unit && <span style={styles.kpiUnit}> {cfg.unit}</span>}
        </div>
        <div style={styles.kpiSubtitle}>{deviceName(ids[0])}</div>
        {freshness && <div style={{ ...styles.kpiSubtitle, color: latest?.stale ? '#F5A623' : 'var(--text-muted)' }}>{freshness}</div>}
      </div>
    );
  }

  if (widget.type === 'timeseries') {
    if (cfg.chart_mode === 'runtime_histogram') {
      const runtimeSeries = (data.series || []) as Array<{ time: string; device_id: string; value?: number | string | null; state?: string | null; duration_seconds?: number }>;
      const runtimeModel = getRuntimeStateModel(cfg);
      const rows = buildRuntimeHistogramRows(runtimeSeries, ids, cfg, runtimeModel);
      if (!rows.length) return <div style={styles.placeholder}>No runtime buckets available</div>;

      const stateTotals = runtimeModel.allStates.map((state) => ({
        ...state,
        total: rows.reduce((sum, row) => sum + Number(row[state.key] || 0), 0),
      }));
      const primaryState = runtimeModel.conditions[0];
      const primaryTotal = primaryState ? stateTotals.find((state) => state.key === primaryState.key)?.total || 0 : 0;
      const measured = stateTotals
        .filter((state) => state.kind !== 'unknown')
        .reduce((sum, state) => sum + state.total, 0);
      const availability = measured > 0 ? Math.round((primaryTotal / measured) * 100) : 0;
      const maxHours = Math.max(...rows.map((row) => row.capacity), 1);
      const condition = runtimeModel.conditions.length === 1
        ? `${cfg.metric || 'metric'} ${runtimeModel.conditions[0].operator || '>'} ${runtimeModel.conditions[0].value ?? '0'}`
        : `${runtimeModel.conditions.length} state rules`;
      const stateLabels = Object.fromEntries(runtimeModel.allStates.map((state) => [state.key, state.label]));

      return (
        <div className="sd-builder-runtime-content">
          <div className="sd-builder-runtime-head">
            <div>
              <span>Condition</span>
              <strong>{condition}</strong>
            </div>
            <div className="sd-builder-runtime-kpis">
              {stateTotals
                .filter((state) => state.kind !== 'unknown' || state.total > 0)
                .slice(0, 4)
                .map((state) => (
                  <span
                    key={state.key}
                    className={state.kind === 'unknown' ? 'is-unknown' : state.kind === 'condition' ? 'is-on' : ''}
                    style={{ '--runtime-color': state.color } as React.CSSProperties}
                  >
                    {state.total.toFixed(1)}h {state.label}
                  </span>
                ))}
              <span>{availability}% {primaryState?.label || 'matched'}</span>
            </div>
          </div>
          <div className="sd-builder-runtime-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={rows} margin={{ top: 8, right: 10, left: 6, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" vertical={false} />
                <XAxis dataKey="label" stroke="#6b7280" fontSize={10} tickLine={false} axisLine={false} minTickGap={20} />
                <YAxis
                  stroke="#6b7280"
                  fontSize={10}
                  tickLine={false}
                  axisLine={false}
                  width={44}
                  domain={[0, maxHours]}
                  tickFormatter={(value) => `${formatAxisTick(value)}h`}
                />
                <Tooltip
                  contentStyle={{
                    background: '#161922',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: '10px',
                    color: '#f8fafc',
                    fontSize: '0.8rem',
                  }}
                  itemStyle={{ color: '#f8fafc' }}
                  labelStyle={{ color: '#9ca3af', marginBottom: '4px' }}
                  formatter={(value: any, name: any) => {
                    const label = stateLabels[String(name)] || String(name);
                    return [`${Number(value || 0).toFixed(2)}h`, label];
                  }}
                />
                {runtimeModel.allStates.map((state, index) => (
                  <Bar
                    key={state.key}
                    dataKey={state.key}
                    name={state.key}
                    stackId="runtime"
                    fill={state.color}
                    fillOpacity={state.kind === 'unknown' ? 0.42 : state.kind === 'fallback' ? 0.68 : 0.9}
                    radius={index === 0 ? [4, 4, 0, 0] : undefined}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      );
    }

    const series = (data.series || []) as Array<{ time: string; device_id: string; value?: number; metric?: string; series_key?: string }>;
    if (!series.length) return <div style={styles.placeholder}>No data in range</div>;
    const seriesKeys = Array.from(new Set(series.map((p) => p.series_key || p.device_id)));
    const seriesLabels: Record<string, string> = {};
    series.forEach((p) => {
      const key = p.series_key || p.device_id;
      seriesLabels[key] = p.metric ? `${deviceName(p.device_id)} - ${p.metric}` : deviceName(p.device_id);
    });
    const byTime = new Map<string, Record<string, any>>();
    series.forEach((p) => {
      const key = p.series_key || p.device_id;
      const row = byTime.get(p.time) || { time: p.time };
      row[key] = p.value;
      byTime.set(p.time, row);
    });
    const rows = Array.from(byTime.values()).sort((a, b) => String(a.time).localeCompare(String(b.time)));
    const numericValues = series
      .map((p) => p.value)
      .filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
    const thresholdValues = cfg.chart_mode === 'threshold_trend'
      ? [cfg.thresholds?.warn, cfg.thresholds?.crit]
        .filter((value): value is number => typeof value === 'number' && Number.isFinite(value))
      : [];
    const yAxisDomain = getMetricAxisDomain([...numericValues, ...thresholdValues]);
    const yAxisWidth = numericValues.some((value) => Math.abs(value) >= 1000) ? 62 : 50;
    const rangeHours = cfg.range_hours || 24;
    const fmtTick = (t: string) => {
      const d = new Date(t);
      return rangeHours <= 24
        ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        : d.toLocaleDateString([], { day: '2-digit', month: '2-digit' });
    };
    const tooltipStyle = {
      background: '#161922',
      border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: '10px',
      color: '#f8fafc',
      fontSize: '0.8rem',
    };
    const tooltipFormatter = (value: any, name: any) => [
      `${formatValue(value)}${cfg.unit ? ` ${cfg.unit}` : ''}`,
      seriesLabels[String(name)] || String(name),
    ];

    if (cfg.chart_mode === 'bucket_bar') {
      const bucketRows = buildBucketTrendRows(series, seriesKeys, rangeHours, cfg.aggregation || 'avg');
      if (!bucketRows.length) return <div style={styles.placeholder}>No bucketed data in range</div>;

      return (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={bucketRows} margin={{ top: 8, right: 10, left: 6, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" vertical={false} />
            <XAxis dataKey="label" stroke="#6b7280" fontSize={10} tickLine={false} axisLine={false} minTickGap={24} />
            <YAxis
              stroke="#6b7280"
              fontSize={10}
              tickLine={false}
              axisLine={false}
              width={yAxisWidth}
              tickFormatter={formatAxisTick}
              domain={yAxisDomain}
            />
            <Tooltip
              contentStyle={tooltipStyle}
              itemStyle={{ color: '#f8fafc' }}
              labelStyle={{ color: '#9ca3af', marginBottom: '4px' }}
              formatter={tooltipFormatter}
            />
            {seriesKeys.map((key, i) => (
              <Bar
                key={key}
                dataKey={key}
                name={key}
                fill={SERIES_COLORS[i % SERIES_COLORS.length]}
                fillOpacity={0.86}
                radius={[4, 4, 0, 0]}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      );
    }

    const areaType = cfg.chart_mode === 'step_state' ? 'stepAfter' : 'monotone';
    return (
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={rows} margin={{ top: 5, right: 10, left: 6, bottom: 0 }}>
          <defs>
            {seriesKeys.map((key, i) => (
              <linearGradient key={key} id={`grad-${widget.id}-${i}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={SERIES_COLORS[i % SERIES_COLORS.length]} stopOpacity={0.35} />
                <stop offset="95%" stopColor={SERIES_COLORS[i % SERIES_COLORS.length]} stopOpacity={0.02} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" vertical={false} />
          <XAxis dataKey="time" tickFormatter={fmtTick} stroke="#6b7280" fontSize={10} tickLine={false} axisLine={false} minTickGap={30} />
          <YAxis
            stroke="#6b7280"
            fontSize={10}
            tickLine={false}
            axisLine={false}
            width={yAxisWidth}
            tickFormatter={formatAxisTick}
            domain={yAxisDomain}
          />
          <Tooltip
            contentStyle={tooltipStyle}
            itemStyle={{ color: '#f8fafc' }}
            labelStyle={{ color: '#9ca3af', marginBottom: '4px' }}
            labelFormatter={(t) => new Date(String(t)).toLocaleString()}
            formatter={tooltipFormatter}
          />
          {cfg.chart_mode === 'threshold_trend' && cfg.thresholds?.warn != null && (
            <ReferenceLine
              y={cfg.thresholds.warn}
              stroke="#FF9F0A"
              strokeDasharray="5 4"
              strokeOpacity={0.8}
              label={{ value: 'Warn', fill: '#FFB547', fontSize: 10, position: 'insideTopRight' }}
            />
          )}
          {cfg.chart_mode === 'threshold_trend' && cfg.thresholds?.crit != null && (
            <ReferenceLine
              y={cfg.thresholds.crit}
              stroke="#FF453A"
              strokeDasharray="5 4"
              strokeOpacity={0.9}
              label={{ value: 'Critical', fill: '#FF7B72', fontSize: 10, position: 'insideTopRight' }}
            />
          )}
          {seriesKeys.map((key, i) => (
            <Area
              key={key}
              type={areaType}
              dataKey={key}
              name={key}
              stroke={SERIES_COLORS[i % SERIES_COLORS.length]}
              strokeWidth={cfg.chart_mode === 'step_state' ? 2.4 : 2}
              fill={`url(#grad-${widget.id}-${i})`}
              dot={cfg.chart_mode === 'step_state' ? { r: 2, strokeWidth: 0 } : false}
              connectNulls
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    );
  }

  if (widget.type === 'gauge') {
    const latest = data.latest.find((l) => l.device_id === ids[0]) || data.latest[0];
    const value = typeof latest?.value === 'number' ? latest.value : null;
    const max = cfg.max && cfg.max > 0 ? cfg.max : 100;
    const frac = value == null ? 0 : clamp(value / max, 0, 1);
    const r = 70;
    const cx = 90;
    const cy = 90;
    const arcLen = Math.PI * r;
    const arc = `M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`;

    let color = '#9ca3af';
    if (value != null) {
      if (activeAlarm) {
        color = activeAlarm.threshold_type === 'crit' ? '#ef4444' : '#F5A623';
      } else {
        const colors = resolveGaugeColors(cfg);
        if (frac >= 0.75) color = colors.high;
        else if (frac >= 0.5) color = colors.midHigh;
        else if (frac >= 0.25) color = colors.midLow;
        else color = colors.low;
      }
    }
    return (
      <div className={`sd-builder-gauge-content ${activeAlarm ? 'has-alarm-action' : ''}`} style={styles.gaugeWrap}>
        <svg viewBox="0 0 180 105" style={{ width: '100%', maxHeight: '100%' }}>
          <path d={arc} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={12} strokeLinecap="round" />
          <path
            className="sd-builder-gauge-progress"
            d={arc}
            fill="none"
            stroke={color}
            strokeWidth={12}
            strokeLinecap="round"
            strokeDasharray={`${arcLen * frac} ${arcLen}`}
          />
          <text x={cx} y={cy - 8} textAnchor="middle" fill="#f8fafc" fontSize={22} fontWeight={800}>
            {formatValue(value)}
          </text>
          <text x={cx} y={cy + 10} textAnchor="middle" fill="#9ca3af" fontSize={10}>
            {`${cfg.unit || ''} / ${max}`.trim()}
          </text>
        </svg>
        {activeAlarm && onAcknowledge && (
          <button
            className={`sd-builder-gauge-ack ${activeAlarm.threshold_type === 'crit' ? 'is-critical' : 'is-warning'}`}
            onClick={() => onAcknowledge(activeAlarm.id)}
          >
            ACKNOWLEDGE
          </button>
        )}
      </div>
    );
  }

  if (widget.type === 'tank') {
    const latest = data.latest.find((l) => l.device_id === ids[0]) || data.latest[0];
    const value = typeof latest?.value === 'number' ? latest.value : null;
    const freshness = formatLatestFreshness(latest);
    const max = cfg.max && cfg.max > 0 ? cfg.max : 100;
    const hasReading = value != null;
    const pct = hasReading ? clamp((value / max) * 100, 0, 100) : 0;
    const fillColor = cfg.tank_fill_color || '#3b82f6';
    const isLow = hasReading && pct <= (cfg.tank_low_threshold ?? 25);
    const isHigh = hasReading && pct >= (cfg.tank_high_threshold ?? 90);
    const levelColor = isLow ? '#ef4444' : isHigh ? '#2DD4A8' : fillColor;
    const tankState = !hasReading ? 'No reading' : isLow ? 'Low reserve' : isHigh ? 'High level' : 'Flow steady';
    const tankStyle = {
      ...styles.tankWrap,
      '--tank-level': `${pct}%`,
      '--tank-color': levelColor,
    } as React.CSSProperties;

    return (
      <div className={`sd-builder-tank-content ${isLow ? 'is-low' : isHigh ? 'is-high' : 'is-steady'}`} style={tankStyle}>
        <div className="sd-builder-tank-readout">
          <div>
            <strong>{formatValue(value)}</strong>
            {cfg.unit && <span>{cfg.unit}</span>}
          </div>
          <em>{pct.toFixed(0)}% capacity</em>
        </div>
        <div className="sd-builder-tank-stage">
          <div className="sd-builder-tank-vessel" aria-label={`Tank level ${pct.toFixed(0)} percent`}>
            <div className="sd-builder-tank-liquid">
              <span className="sd-builder-tank-wave is-back" />
              <span className="sd-builder-tank-wave is-front" />
              <span className="sd-builder-tank-bubble is-one" />
              <span className="sd-builder-tank-bubble is-two" />
              <span className="sd-builder-tank-bubble is-three" />
            </div>
            <div className="sd-builder-tank-markers">
              {[100, 75, 50, 25, 0].map((t) => (
                <span key={t}>
                  <b>{t}%</b>
                </span>
              ))}
            </div>
            <span className="sd-builder-tank-highlight" />
          </div>
          <div className="sd-builder-tank-side">
            <span className="sd-builder-tank-state">
              <i />
              {tankState}
            </span>
            {freshness && <span className={`sd-builder-tank-freshness ${latest?.stale ? 'is-stale' : ''}`}>{freshness}</span>}
            <div className="sd-builder-tank-capacity">
              <b>{max}</b>
              <span>{cfg.unit || 'max'}</span>
            </div>
            <div className="sd-builder-tank-pipe" aria-hidden="true">
              <span />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // state
  const latest = data.latest.find((l) => l.device_id === ids[0]) || data.latest[0];
  const stateVal = latest?.value != null ? String(latest.value) : null;
  const positive = /^(on|true|open|active|running|ok|1)$/i.test(stateVal || '');
  const negative = /^(off|false|closed|inactive|stopped|error|fault|0)$/i.test(stateVal || '');
  const badgeColor = positive ? '#2DD4A8' : negative ? '#ef4444' : '#94a3b8';
  const duration = formatDuration(latest?.state_duration_seconds);
  const freshness = formatLatestFreshness(latest);
  return (
    <div className="sd-builder-state-content" style={styles.kpiWrap}>
      <span
        style={{
          ...styles.stateBadge,
          background: `${badgeColor}20`,
          color: badgeColor,
          borderColor: `${badgeColor}40`,
        }}
      >
        {stateVal ?? '—'}
      </span>
      {duration && <div style={styles.kpiSubtitle}>for {duration}</div>}
      <div style={styles.kpiSubtitle}>{deviceName(ids[0])}</div>
      {freshness && <div style={{ ...styles.kpiSubtitle, color: latest?.stale ? '#F5A623' : 'var(--text-muted)' }}>{freshness}</div>}
    </div>
  );
}

function ConsumptionWidget({ widget, devices }: { widget: DashboardWidget; devices: Device[] }) {
  const cfg = widget.config;
  const ids = cfg.device_ids || [];
  const [data, setData] = useState<ConsumptionDataResponse | null>(null);
  const [error, setError] = useState('');
  const configKey = JSON.stringify(cfg);
  const unit = cfg.unit || '';

  useEffect(() => {
    if (!ids.length || !cfg.metric) return;
    let cancelled = false;
    const load = () => {
      getConsumptionData({
        device_id: ids[0],
        metric: cfg.metric!,
        range_hours: cfg.range_hours || 24,
        bucket_size: getConsumptionBucketSize(cfg.range_hours || 24, cfg.bucket_size),
        mode: cfg.consumption_mode || 'tank_drawdown',
        refill_threshold: cfg.refill_threshold,
        counter_reset_threshold: cfg.counter_reset_threshold,
        rate_time_unit: cfg.rate_time_unit || 'hour',
        tank_capacity: cfg.tank_capacity,
        min_valid: cfg.min_valid,
        max_valid: cfg.max_valid,
        max_gap_minutes: cfg.max_gap_minutes,
        max_daily_consumption: cfg.max_daily_consumption,
      })
        .then((d: ConsumptionDataResponse) => {
          if (!cancelled) {
            setData(d);
            setError('');
          }
        })
        .catch((err: any) => {
          if (!cancelled) setError(err.response?.data?.error || 'Failed to load data');
        });
    };
    load();
    const interval = setInterval(load, 30000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [configKey, ids[0]]);

  if (error) return <div style={styles.widgetError}>{error}</div>;
  if (!data) return <div style={styles.placeholder}>Loading...</div>;

  const analysis = buildBackendConsumptionAnalysis(data, cfg);
  if (analysis.pointCount < 2) {
    return <div style={styles.placeholder}>Need at least two readings in this range to calculate consumption.</div>;
  }

  const deviceName = devices.find((d) => d.id === ids[0])?.name || 'Selected asset';
  const amount = (value: number) => `${formatConsumptionAmount(value)}${unit ? ` ${unit}` : ''}`;
  const startValue = analysis.startValue != null ? amount(analysis.startValue) : 'n/a';
  const endValue = analysis.endValue != null ? amount(analysis.endValue) : 'n/a';
  const modeLabel = getConsumptionModeLabel(analysis.mode);
  const peakCopy = analysis.peakBucket && analysis.peakBucket.consumed > 0
    ? `${analysis.peakBucket.label} - ${amount(analysis.peakBucket.consumed)}`
    : 'No movement';
  const projectedCopy = analysis.projectedEmptyHours
    ? formatDuration(analysis.projectedEmptyHours * 3600)
    : 'No projection';
  const rateUnitLabel = cfg.rate_time_unit || 'hour';
  const coverageCopy = analysis.coverageStart && analysis.coverageEnd
    ? `${formatConsumptionDate(analysis.coverageStart)} to ${formatConsumptionDate(analysis.coverageEnd)}`
    : 'No covered range';
  const rateModeWarning = getConsumptionRateModeWarning(cfg);
  const setupWarnings = Array.from(new Set(analysis.warnings));
  const anomalySummary = formatConsumptionAnomalySummary(analysis.rows);
  const detailItems = analysis.mode === 'tank_drawdown'
    ? [
      ['Refills', `${analysis.refillEvents} / ${amount(analysis.refillVolume)}`],
      ['Projected empty', projectedCopy],
      ['Coverage', coverageCopy],
      ['Data quality', anomalySummary],
    ]
    : analysis.mode === 'cumulative_counter'
      ? [
        ['Counter resets', String(analysis.resetEvents)],
        ['Start / end', `${startValue} -> ${endValue}`],
        ['Coverage', coverageCopy],
        ['Data quality', anomalySummary],
      ]
      : [
        ['Rate basis', `metric per ${rateUnitLabel}`],
        ['Start / end', `${startValue} -> ${endValue}`],
        ['Coverage', coverageCopy],
        ['Data quality', anomalySummary],
      ];

  return (
    <div className="sd-builder-consumption-content">
      {analysis.hasPartialCoverage && (
        <div className="sd-builder-consumption-warning">
          <strong>Partial coverage</strong>
          <span>
            Backend covered {analysis.pointCount} readings from {coverageCopy}. The selected window may have missing telemetry before that range.
          </span>
        </div>
      )}
      {rateModeWarning && (
        <div className="sd-builder-consumption-warning">
          <strong>Mode check</strong>
          <span>{rateModeWarning}</span>
        </div>
      )}
      {setupWarnings.map((warning) => (
        <div className="sd-builder-consumption-warning" key={warning}>
          <strong>Verify setup</strong>
          <span>{warning}</span>
        </div>
      ))}
      {analysis.anomalyCount > 0 && (
        <div className="sd-builder-consumption-warning is-quality">
          <strong>Data quality</strong>
          <span>{anomalySummary}. Review orange buckets before treating the total as final.</span>
        </div>
      )}
      <div className="sd-builder-consumption-summary">
        <div className="sd-builder-consumption-total">
          <span>Total consumed</span>
          <strong>{amount(analysis.totalConsumed)}</strong>
          <em>{deviceName}</em>
        </div>
        <div className="sd-builder-consumption-facts">
          <span>
            <b>{modeLabel}</b>
            mode
          </span>
          <span>
            <b>{amount(analysis.avgPerHour)}</b>
            per covered hour
          </span>
          <span>
            <b>{peakCopy}</b>
            peak bucket
          </span>
        </div>
      </div>

      <div className="sd-builder-consumption-chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={analysis.rows} margin={{ top: 8, right: 10, left: 6, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" vertical={false} />
            <XAxis dataKey="label" stroke="#6b7280" fontSize={10} tickLine={false} axisLine={false} minTickGap={24} />
            <YAxis
              stroke="#6b7280"
              fontSize={10}
              tickLine={false}
              axisLine={false}
              width={56}
              tickFormatter={formatAxisTick}
            />
            <Tooltip
              contentStyle={{
                background: '#161922',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '10px',
                color: '#f8fafc',
                fontSize: '0.8rem',
              }}
              itemStyle={{ color: '#f8fafc' }}
              labelStyle={{ color: '#9ca3af', marginBottom: '4px' }}
              formatter={(value: any, _name: any, item: any) => {
                const row = item?.payload as ConsumptionBucketRow | undefined;
                const reasons = row?.anomalyReasons?.length
                  ? ` (${row.anomalyReasons.map(formatConsumptionAnomalyReason).join(', ')})`
                  : '';
                return [amount(Number(value || 0)), `Consumed${reasons}`];
              }}
            />
            <Bar dataKey="consumed" name="Consumed" fill="#2DD4A8" fillOpacity={0.88} radius={[4, 4, 0, 0]}>
              {analysis.rows.map((row) => (
                <Cell key={row.time} fill={row.anomaly ? '#FF9F0A' : '#2DD4A8'} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="sd-builder-consumption-insights">
        {detailItems.map(([label, value]) => (
          <span key={label} title={value}>
            <b>{label}</b>
            {value}
          </span>
        ))}
      </div>
    </div>
  );
}

function GpsMapWidget({ widget, devices }: { widget: DashboardWidget; devices: Device[] }) {
  const cfg = widget.config;
  const ids = cfg.device_ids || [];
  const [points, setPoints] = useState<{ time: string; device_id: string; lat: number; lng: number }[]>([]);
  const [error, setError] = useState('');
  const configKey = JSON.stringify(cfg);

  useEffect(() => {
    if (!ids.length) return;
    let cancelled = false;
    const load = () => {
      getGpsMapData({
        device_ids: ids,
        latitude_metric: cfg.latitude_metric || 'latitude',
        longitude_metric: cfg.longitude_metric || 'longitude',
        location_metric: cfg.location_metric,
        range_hours: cfg.range_hours || 24,
      })
        .then((d) => {
          if (!cancelled) {
            setPoints(d.points || []);
            setError('');
          }
        })
        .catch((err: any) => {
          if (!cancelled) setError(err.response?.data?.error || 'Failed to load GPS data');
        });
    };
    load();
    const interval = setInterval(load, 30000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [configKey]);

  if (error) return <div style={styles.widgetError}>{error}</div>;
  if (!points.length) return <div style={styles.placeholder}>No GPS points in range</div>;

  const deviceName = (id: string) => devices.find((d) => d.id === id)?.name || id;
  const lats = points.map((p) => p.lat);
  const lngs = points.map((p) => p.lng);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs);
  const maxLng = Math.max(...lngs);
  const pad = 0.0005;
  const latRange = Math.max(maxLat - minLat, 0.001) + pad * 2;
  const lngRange = Math.max(maxLng - minLng, 0.001) + pad * 2;

  function project(lat: number, lng: number) {
    const x = ((lng - (minLng - pad)) / lngRange) * 100;
    const y = 100 - ((lat - (minLat - pad)) / latRange) * 100;
    return { x: clamp(x, 0, 100), y: clamp(y, 0, 100) };
  }

  const byDevice = new Map<string, typeof points>();
  for (const p of points) {
    const list = byDevice.get(p.device_id) || [];
    list.push(p);
    byDevice.set(p.device_id, list);
  }

  const colors = [cfg.trail_color || '#2DD4A8', '#3b82f6', '#F5A623', '#e8602c', '#a78bfa'];

  return (
    <div style={styles.gpsMapWrap}>
      <svg viewBox="0 0 100 100" style={{ width: '100%', height: '100%' }}>
        <rect x={0} y={0} width={100} height={100} fill="rgba(22,25,34,0.4)" />
        {[0, 25, 50, 75, 100].map((t) => (
          <g key={t} stroke="rgba(255,255,255,0.05)">
            <line x1={t} y1={0} x2={t} y2={100} />
            <line x1={0} y1={t} x2={100} y2={t} />
          </g>
        ))}
        {Array.from(byDevice.entries()).map(([deviceId, pts], idx) => {
          const color = colors[idx % colors.length];
          const d = pts
            .map((p, i) => {
              const { x, y } = project(p.lat, p.lng);
              return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
            })
            .join(' ');
          const last = pts[pts.length - 1];
          const lastProj = project(last.lat, last.lng);
          return (
            <g key={deviceId}>
              <path className="sd-builder-gps-trail" d={d} fill="none" stroke={color} strokeWidth={0.8} strokeLinecap="round" strokeLinejoin="round" opacity={0.7} />
              {pts.map((p, i) => {
                const { x, y } = project(p.lat, p.lng);
                const isLast = i === pts.length - 1;
                return (
                  <circle
                    className={isLast ? 'sd-builder-gps-current' : undefined}
                    key={i}
                    cx={x}
                    cy={y}
                    r={isLast ? 1.8 : 0.6}
                    fill={isLast ? color : 'rgba(255,255,255,0.3)'}
                    stroke={isLast ? '#fff' : 'none'}
                    strokeWidth={0.3}
                  />
                );
              })}
              <text x={lastProj.x + 2} y={lastProj.y - 2} fill={color} fontSize={3} fontWeight={700}>
                {deviceName(deviceId)}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function AlertsWidget() {
  const [logs, setLogs] = useState<AlertLog[]>([]);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      getAlertLogs()
        .then((d: AlertLog[]) => {
          if (!cancelled) {
            setLogs(Array.isArray(d) ? d : []);
            setError('');
          }
        })
        .catch((err: any) => {
          if (!cancelled) setError(err.response?.data?.error || 'Failed to load alerts');
        });
    };
    load();
    const interval = setInterval(load, 30000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  if (error) return <div style={styles.widgetError}>{error}</div>;

  const rows = [...logs]
    .sort((a, b) => String(b.triggered_at).localeCompare(String(a.triggered_at)))
    .slice(0, 6);

  if (!rows.length) return <div style={styles.placeholder}>No recent alerts</div>;

  return (
    <div style={styles.alertList}>
      {rows.map((log) => (
        <div key={log.id} style={styles.alertRow}>
          <span style={styles.alertTime}>
            {new Date(log.triggered_at).toLocaleString([], { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
          </span>
          <span style={styles.alertDevice}>{log.device_name || '—'}</span>
          <span style={styles.alertMsg}>
            {log.alert_name ? `${log.alert_name}: ` : ''}{log.metric} {log.operator} {log.threshold} (got {log.actual_value})
          </span>
        </div>
      ))}
    </div>
  );
}

function StatusWidget({ widget }: { widget: DashboardWidget }) {
  const [devices, setDevices] = useState<Device[]>([]);
  const [error, setError] = useState('');
  const ids = widget.config.device_ids || [];

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      getDevices()
        .then((d: Device[]) => {
          if (!cancelled) {
            setDevices(Array.isArray(d) ? d : []);
            setError('');
          }
        })
        .catch((err: any) => {
          if (!cancelled) setError(err.response?.data?.error || 'Failed to load devices');
        });
    };
    load();
    const interval = setInterval(load, 30000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  if (error) return <div style={styles.widgetError}>{error}</div>;

  const selected = devices.filter((d) => ids.includes(d.id));
  if (!selected.length) return <div style={styles.placeholder}>No matching devices</div>;

  const onlineCount = selected.filter((d) => d.status === 'online').length;
  const offlineCount = selected.length - onlineCount;
  const healthPct = selected.length ? Math.round((onlineCount / selected.length) * 100) : 0;

  return (
    <div className="sd-builder-device-status-content">
      <div className="sd-builder-device-status-summary">
        <div>
          <span>Fleet Health</span>
          <strong>{onlineCount}/{selected.length}</strong>
        </div>
        <div className="sd-builder-device-health-ring" style={{ '--health-pct': `${healthPct * 3.6}deg` } as React.CSSProperties}>
          <b>{healthPct}%</b>
        </div>
        <div className="sd-builder-device-status-counts">
          <span className="is-online">{onlineCount} online</span>
          <span className="is-offline">{offlineCount} offline</span>
        </div>
      </div>

      <div className="sd-builder-device-status-list">
        {selected.map((d) => {
          const isOnline = d.status === 'online';
          const location = d.location || d.area_name || d.site_name || 'No location set';
          return (
            <div key={d.id} className={`sd-builder-device-status-row ${isOnline ? 'is-online' : 'is-offline'}`}>
              <span className="sd-builder-device-status-rail" aria-hidden="true" />
              <span className="sd-builder-status-dot" aria-hidden="true" />
              <div className="sd-builder-device-status-main">
                <strong>{d.name}</strong>
                <span>{location}</span>
              </div>
              <div className="sd-builder-device-status-meta">
                <span className="sd-builder-device-status-badge">{isOnline ? 'Online' : 'Offline'}</span>
                <time title={d.last_reading_at ? new Date(d.last_reading_at).toLocaleString() : 'No readings yet'}>
                  {formatCompactTime(d.last_reading_at)}
                </time>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Template gallery modal
// ---------------------------------------------------------------------------

interface TemplateGalleryModalProps {
  canCreateDashboard: boolean;
  createDisabledReason?: string;
  hasCurrent: boolean;
  onClose: () => void;
  onPick: (templateId: string, name: string) => void;
  onSaveCurrent: (name: string, industry: string, description: string) => Promise<void>;
}

function TemplateGalleryModal({ canCreateDashboard, createDisabledReason, hasCurrent, onClose, onPick, onSaveCurrent }: TemplateGalleryModalProps) {
  const [templates, setTemplates] = useState<DashboardTemplate[]>([]);
  const [error, setError] = useState('');

  const load = () => {
    getDashboardTemplates()
      .then((d: DashboardTemplate[]) => setTemplates(Array.isArray(d) ? d : []))
      .catch((err: any) => setError(err.response?.data?.error || 'Failed to load templates'));
  };

  useEffect(load, []);

  async function handleDeleteTemplate(t: DashboardTemplate) {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try {
      await deleteDashboardTemplate(t.id);
      load();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to delete template');
    }
  }

  async function handleSaveCurrent() {
    const name = window.prompt('Template name', '');
    if (!name) return;
    const industry = window.prompt('Industry (optional)', '') || '';
    const description = window.prompt('Description (optional)', '') || '';
    try {
      await onSaveCurrent(name, industry, description);
      load();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to save template');
    }
  }

  return (
    <div className="sd-builder-modal-overlay" onClick={onClose}>
      <div className="sd-builder-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="sd-builder-modal-header">
          <div>
            <span>Template Library</span>
            <h3>Dashboard Templates</h3>
          </div>
          <button className="sd-builder-icon-button" onClick={onClose} type="button">Close</button>
        </div>
        {hasCurrent && (
          <button className="sd-builder-button sd-builder-template-save" onClick={handleSaveCurrent} type="button">
            Save current dashboard as template
          </button>
        )}
        {error && <div style={styles.widgetError}>{error}</div>}
        <div className="sd-builder-template-grid">
          {templates.map((t) => (
            <article key={t.id} className="sd-builder-template-card">
              <div className="sd-builder-template-head">
                <strong>{t.name}</strong>
                {t.is_builtin
                  ? <span style={styles.builtinBadge}>Built-in</span>
                  : (
                    <button
                      className="sd-builder-icon-button is-danger"
                      type="button"
                      title="Delete template"
                      onClick={() => handleDeleteTemplate(t)}
                    >
                      Delete
                    </button>
                  )}
              </div>
              {t.industry && <div style={styles.templateIndustry}>{t.industry}</div>}
              {t.description && <p style={styles.templateDesc}>{t.description}</p>}
              <div className="sd-builder-template-count">
                {t.layout?.widgets?.length || 0} widgets
              </div>
              <button
                className="sd-builder-button is-primary"
                disabled={!canCreateDashboard}
                type="button"
                title={canCreateDashboard ? 'Create dashboard from template' : createDisabledReason}
                onClick={() => {
                  if (!canCreateDashboard) return;
                  const name = window.prompt('Name for the new dashboard', t.name);
                  if (name) onPick(t.id, name);
                }}
              >
                Use Template
              </button>
            </article>
          ))}
          {!templates.length && !error && (
            <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>No templates available.</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Widget config modal (add + edit)
// ---------------------------------------------------------------------------

interface WidgetConfigModalProps {
  widget: DashboardWidget;
  devices: Device[];
  onSave: (w: DashboardWidget) => void;
  onClose: () => void;
}

function WidgetConfigModal({ widget, devices, onSave, onClose }: WidgetConfigModalProps) {
  const [type, setType] = useState<DashboardWidgetType>(widget.type);
  const [cfg, setCfg] = useState<WidgetConfig>({ ...widget.config });
  const [metrics, setMetrics] = useState<DeviceMetric[]>([]);
  const [metricsByDevice, setMetricsByDevice] = useState<Record<string, DeviceMetric[]>>({});

  const ids = cfg.device_ids || [];
  const idsKey = ids.join('|');
  const firstId = ids[0] || '';
  const isSingleDevice = SINGLE_DEVICE_TYPES.includes(type);
  const usesMetric = METRIC_TYPES.includes(type);
  const isNumericMetric = type !== 'state';
  const chartMode: TimeSeriesChartMode = cfg.chart_mode || 'line';
  const isConsumption = type === 'consumption';
  const isRuntimeMode = type === 'timeseries' && chartMode === 'runtime_histogram';
  const isTrendTimeSeries = type === 'timeseries' && !isRuntimeMode;
  const isThresholdTrend = type === 'timeseries' && chartMode === 'threshold_trend';

  useEffect(() => {
    if (!firstId || !usesMetric || isTrendTimeSeries) {
      setMetrics([]);
      return;
    }
    getDeviceMetrics(firstId)
      .then((r: any) => setMetrics(r.metrics || []))
      .catch(() => setMetrics([]));
  }, [firstId, isTrendTimeSeries, type, usesMetric]);

  useEffect(() => {
    if (type !== 'timeseries' || isRuntimeMode || !ids.length) {
      setMetricsByDevice({});
      return;
    }

    let cancelled = false;
    Promise.all(
      ids.map((deviceId) => (
        getDeviceMetrics(deviceId)
          .then((r: any) => [deviceId, r.metrics || []] as const)
          .catch(() => [deviceId, []] as const)
      )),
    ).then((entries) => {
      if (!cancelled) setMetricsByDevice(Object.fromEntries(entries));
    });

    return () => {
      cancelled = true;
    };
  }, [idsKey, isRuntimeMode, type]);

  const set = (patch: Partial<WidgetConfig>) => setCfg((prev) => ({ ...prev, ...patch }));

  function toggleDevice(id: string) {
    const removing = ids.includes(id);
    const nextIds = removing ? ids.filter((d) => d !== id) : [...ids, id];
    const nextMetricsByDevice = { ...(cfg.metric_by_device || {}) };
    if (removing) delete nextMetricsByDevice[id];
    const nextMetric = isTrendTimeSeries
      ? nextIds.map((deviceId) => nextMetricsByDevice[deviceId]).find(Boolean)
      : cfg.metric;
    set({
      device_ids: nextIds,
      ...(isTrendTimeSeries ? { metric_by_device: nextMetricsByDevice, metric: nextMetric } : {}),
    });
  }

  function handleTypeChange(t: DashboardWidgetType) {
    setType(t);
    // Keep config, but drop device selection semantics that no longer apply.
    const patch: Partial<WidgetConfig> = {};
    if (SINGLE_DEVICE_TYPES.includes(t) && ids.length > 1) patch.device_ids = ids.slice(0, 1);
    if (t === 'consumption') {
      patch.consumption_mode = cfg.consumption_mode || 'tank_drawdown';
      patch.aggregation = 'last';
      patch.range_hours = cfg.range_hours || 24;
      patch.unit = cfg.unit || 'L';
      patch.rate_time_unit = cfg.rate_time_unit || 'hour';
    }
    if (Object.keys(patch).length) set(patch);
  }

  function applyChartMode(mode: TimeSeriesChartMode) {
    set({
      chart_mode: mode,
      ...(mode === 'runtime_histogram'
        ? {
          metric_by_device: {},
          aggregation: 'last',
          range_hours: cfg.range_hours || 168,
          condition_operator: cfg.condition_operator || '>',
          condition_value: cfg.condition_value ?? '0',
          true_label: cfg.true_label || 'ON',
          false_label: cfg.false_label || 'OFF',
          true_color: cfg.true_color || '#30D158',
          false_color: cfg.false_color || '#FF453A',
          unknown_color: cfg.unknown_color || '#64748b',
          unmatched_runtime_behavior: cfg.unmatched_runtime_behavior || 'fallback',
          runtime_states: cfg.runtime_states || getRuntimeEditorStates(cfg),
          bucket_size: cfg.bucket_size || 'auto',
        }
        : {
          range_hours: cfg.range_hours || 24,
        }),
    });
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const nextConfig = type === 'consumption'
      ? {
        ...cfg,
        aggregation: 'last' as const,
        consumption_mode: cfg.consumption_mode || 'tank_drawdown',
        range_hours: cfg.range_hours || 24,
        rate_time_unit: cfg.rate_time_unit || 'hour',
      }
      : cfg;
    onSave({ ...widget, type, config: nextConfig });
  }

  const metricOptions = metrics.some((m) => m.key === cfg.metric)
    ? metrics
    : ensureMetricOption(metrics, cfg.metric);
  const runtimeEditorStates = getRuntimeEditorStates(cfg);
  const selectedTimeSeriesStyle = getTimeSeriesStyleOption(chartMode);
  const rateModeWarning = getConsumptionRateModeWarning(cfg);

  function setRuntimeStates(nextStates: RuntimeCondition[]) {
    set({
      runtime_states: nextStates,
      true_label: nextStates[0]?.label || cfg.true_label,
      condition_operator: nextStates[0]?.operator || cfg.condition_operator,
      condition_value: nextStates[0]?.value ?? cfg.condition_value,
      true_color: nextStates[0]?.color || cfg.true_color,
    });
  }

  function updateRuntimeState(index: number, patch: Partial<RuntimeCondition>) {
    setRuntimeStates(runtimeEditorStates.map((state, i) => (i === index ? { ...state, ...patch } : state)));
  }

  function addRuntimeState() {
    const index = runtimeEditorStates.length;
    setRuntimeStates([
      ...runtimeEditorStates,
      {
        id: `runtime-state-${Date.now()}`,
        label: `State ${index + 1}`,
        operator: '==',
        value: '',
        color: RUNTIME_STATE_COLORS[index % RUNTIME_STATE_COLORS.length],
      },
    ]);
  }

  function removeRuntimeState(index: number) {
    if (runtimeEditorStates.length <= 1) return;
    setRuntimeStates(runtimeEditorStates.filter((_, i) => i !== index));
  }

  return (
    <div className="sd-builder-modal-overlay" onClick={onClose}>
      <div className="sd-builder-modal-card sd-builder-config-card" onClick={(e) => e.stopPropagation()}>
        <div className="sd-builder-modal-header">
          <div>
            <span>Widget Studio</span>
            <h3>Configure Widget</h3>
          </div>
          <button className="sd-builder-icon-button" onClick={onClose} type="button">Close</button>
        </div>
        <form onSubmit={handleSubmit}>
          <label style={styles.fieldLabel}>Type</label>
          <div className="sd-builder-type-picker">
            {WIDGET_TYPES.map((t) => (
              <button
                key={t.type}
                type="button"
                onClick={() => handleTypeChange(t.type)}
                className={`sd-builder-type-button ${type === t.type ? 'is-selected' : ''}`}
              >
                <span className="sd-builder-type-icon" aria-hidden="true">
                  <WidgetTypeIcon type={t.type} />
                </span>
                <div className="sd-builder-type-copy">
                  <strong>{t.label}</strong>
                  <small>{t.summary}</small>
                </div>
              </button>
            ))}
          </div>

          <label style={styles.fieldLabel}>Title</label>
          <input
            style={styles.input}
            value={cfg.title || ''}
            onChange={(e) => set({ title: e.target.value })}
            placeholder={`e.g. ${TYPE_LABELS[type]}`}
          />
          <p className="sd-builder-inline-help">Shown in the widget header. Leave blank to use the type name.</p>

          {type === 'timeseries' && (
            <>
              <label style={styles.fieldLabel}>Chart style</label>
              <div className="sd-builder-chart-style-grid">
                {TIME_SERIES_STYLE_OPTIONS.map((option) => (
                  <button
                    key={option.mode}
                    type="button"
                    className={`sd-builder-chart-style-card ${chartMode === option.mode ? 'is-selected' : ''}`}
                    onClick={() => applyChartMode(option.mode)}
                  >
                    <span className={`sd-builder-chart-style-preview is-${option.preview}`} aria-hidden="true">
                      <i />
                      <i />
                      <i />
                      <i />
                    </span>
                    <span className="sd-builder-chart-style-copy">
                      <em>{option.eyebrow}</em>
                      <strong>{option.label}</strong>
                      <small>{option.description}</small>
                    </span>
                  </button>
                ))}
              </div>
              <div className={`sd-builder-mode-guide is-${selectedTimeSeriesStyle.preview}`}>
                <div>
                  <span>{selectedTimeSeriesStyle.eyebrow}</span>
                  <strong>{selectedTimeSeriesStyle.label}</strong>
                  <p>{selectedTimeSeriesStyle.description}</p>
                </div>
                <ul>
                  {selectedTimeSeriesStyle.bullets.map((bullet) => (
                    <li key={bullet}>{bullet}</li>
                  ))}
                </ul>
              </div>
            </>
          )}

          {type !== 'alerts' && type !== 'text' && (
            <>
              <label style={styles.fieldLabel}>{isSingleDevice ? 'Device' : 'Devices'}</label>
              {isSingleDevice ? (
                <select
                  style={styles.input}
                  value={firstId}
                  onChange={(e) => set({ device_ids: e.target.value ? [e.target.value] : [], metric: undefined, metric_by_device: {} })}
                >
                  <option value="">Select a device...</option>
                  {devices.map((d) => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              ) : (
                <div style={styles.deviceCheckList}>
                  {devices.map((d) => (
                    <label key={d.id} style={styles.deviceCheckRow}>
                      <input
                        type="checkbox"
                        checked={ids.includes(d.id)}
                        onChange={() => toggleDevice(d.id)}
                      />
                      {d.name}
                    </label>
                  ))}
                  {!devices.length && <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>No devices registered.</span>}
                </div>
              )}
              <div className="sd-builder-config-note">
                {isRuntimeMode
                  ? 'Pick one source asset for runtime logic so each state rule is clear.'
                  : isConsumption
                    ? 'Pick the asset whose readings should be converted into consumption for this widget.'
                    : isTrendTimeSeries
                    ? 'Pick the assets to compare. Each selected asset gets its own metric selector below.'
                    : 'Select the asset that feeds this widget. Metric options come from its mappings and recent telemetry.'}
              </div>
            </>
          )}

          {isTrendTimeSeries && ids.length > 0 && (
            <>
              <label style={styles.fieldLabel}>Metrics by device</label>
              <div className="sd-builder-device-metric-list">
                {ids.map((deviceId) => {
                  const device = devices.find((d) => d.id === deviceId);
                  const hasDeviceSpecificMetrics = Object.values(cfg.metric_by_device || {}).some(Boolean);
                  const selectedMetric = hasDeviceSpecificMetrics
                    ? cfg.metric_by_device?.[deviceId] || ''
                    : cfg.metric_by_device?.[deviceId] || cfg.metric || '';
                  const options = ensureMetricOption(metricsByDevice[deviceId] || [], selectedMetric);

                  return (
                    <div key={deviceId} className="sd-builder-device-metric-row">
                      <div className="sd-builder-device-metric-source">
                        <strong>{device?.name || 'Unknown device'}</strong>
                        <span>{device?.topic || deviceId}</span>
                      </div>
                      <select
                        style={styles.input}
                        value={selectedMetric}
                        onChange={(e) => {
                          const metric = e.target.value || undefined;
                          const hasSpecificMetrics = Object.values(cfg.metric_by_device || {}).some(Boolean);
                          const nextMetricsByDevice: Record<string, string> = hasSpecificMetrics
                            ? { ...(cfg.metric_by_device || {}) }
                            : Object.fromEntries(
                              ids
                                .map((id) => [id, cfg.metric || ''] as const)
                                .filter(([, value]) => !!value),
                            );

                          if (metric) nextMetricsByDevice[deviceId] = metric;
                          else delete nextMetricsByDevice[deviceId];

                          set({
                            metric_by_device: nextMetricsByDevice,
                            metric: Object.values(nextMetricsByDevice).find(Boolean) || undefined,
                          });
                        }}
                      >
                        <option value="">Select metric...</option>
                        {options.map((m) => (
                          <option key={m.key} value={m.key}>
                            {m.key}{m.numeric ? '' : ' (text)'}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>
              <div className="sd-builder-config-note">
                Choose the metric each selected device contributes to the trend. This allows one chart to compare the same metric across multiple assets.
              </div>
            </>
          )}

          {isRuntimeMode && firstId && (
            <>
              <label style={styles.fieldLabel}>Condition metric</label>
              <select
                style={styles.input}
                value={cfg.metric || ''}
                onChange={(e) => set({ metric: e.target.value || undefined })}
              >
                <option value="">Select a metric...</option>
                {metricOptions.map((m) => (
                  <option key={m.key} value={m.key}>
                    {m.key}{m.numeric ? '' : ' (text)'}
                  </option>
                ))}
              </select>
              <div className="sd-builder-config-note">
                This metric is evaluated against the state rules below to calculate runtime hours.
              </div>
            </>
          )}

          {usesMetric && firstId && type !== 'timeseries' && (
            <>
              <label style={styles.fieldLabel}>Metric</label>
              <select
                style={styles.input}
                value={cfg.metric || ''}
                onChange={(e) => set({ metric: e.target.value || undefined })}
              >
                <option value="">Select a metric...</option>
                {metricOptions.map((m) => (
                  <option key={m.key} value={m.key}>
                    {m.key}{m.numeric ? '' : ' (text)'}
                  </option>
                ))}
              </select>
              <div className="sd-builder-config-note">
                Metric value displayed by this widget. Numeric metrics support units, aggregation, ranges, and alarms.
              </div>
            </>
          )}

          {isRuntimeMode && (
            <>
              <label style={styles.fieldLabel}>Runtime state rules</label>
              <div className="sd-builder-runtime-rule-list">
                {runtimeEditorStates.map((state, index) => (
                  <div key={state.id || index} className="sd-builder-runtime-rule">
                    <div className="sd-builder-runtime-rule-head">
                      <span>Rule {index + 1}</span>
                      <button
                        className="sd-builder-icon-button is-danger"
                        type="button"
                        onClick={() => removeRuntimeState(index)}
                        disabled={runtimeEditorStates.length <= 1}
                      >
                        Remove
                      </button>
                    </div>
                    <div className="sd-builder-runtime-rule-grid">
                      <label>
                        <span>Label</span>
                        <input
                          style={styles.input}
                          value={state.label}
                          onChange={(e) => updateRuntimeState(index, { label: e.target.value })}
                          placeholder="ON, Running, Idle..."
                        />
                      </label>
                      <label>
                        <span>Operator</span>
                        <select
                          style={styles.input}
                          value={state.operator}
                          onChange={(e) => updateRuntimeState(index, { operator: e.target.value as RuntimeCondition['operator'] })}
                        >
                          <option value=">">Greater than</option>
                          <option value=">=">Greater or equal</option>
                          <option value="<">Less than</option>
                          <option value="<=">Less or equal</option>
                          <option value="==">Equals</option>
                          <option value="!=">Not equals</option>
                          <option value="contains">Contains text</option>
                        </select>
                      </label>
                      <label>
                        <span>Value</span>
                        <input
                          style={styles.input}
                          value={state.value}
                          onChange={(e) => updateRuntimeState(index, { value: e.target.value })}
                          placeholder="0, ON, running..."
                        />
                      </label>
                      <label>
                        <span>Color</span>
                        <input
                          className="sd-builder-runtime-color"
                          type="color"
                          value={state.color}
                          onChange={(e) => updateRuntimeState(index, { color: e.target.value })}
                        />
                      </label>
                    </div>
                  </div>
                ))}
                <button className="sd-builder-button is-template" type="button" onClick={addRuntimeState}>
                  Add state rule
                </button>
              </div>

              <label style={styles.fieldLabel}>Fallback handling</label>
              <div style={styles.fieldRow}>
                <div style={{ flex: 1 }}>
                  <label style={{ ...styles.fieldLabel, fontSize: '0.7rem' }}>Unmatched readings</label>
                  <select
                    style={styles.input}
                    value={cfg.unmatched_runtime_behavior || 'fallback'}
                    onChange={(e) => set({ unmatched_runtime_behavior: e.target.value as WidgetConfig['unmatched_runtime_behavior'] })}
                  >
                    <option value="fallback">Count as fallback label</option>
                    <option value="unknown">Count as no telemetry</option>
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ ...styles.fieldLabel, fontSize: '0.7rem' }}>No telemetry color</label>
                  <input
                    className="sd-builder-runtime-color is-wide"
                    type="color"
                    value={cfg.unknown_color || '#64748b'}
                    onChange={(e) => set({ unknown_color: e.target.value })}
                  />
                </div>
              </div>

              {(cfg.unmatched_runtime_behavior || 'fallback') === 'fallback' && (
                <div style={styles.fieldRow}>
                  <div style={{ flex: 1 }}>
                    <label style={styles.fieldLabel}>Fallback label</label>
                    <input
                      style={styles.input}
                      value={cfg.false_label || 'OFF'}
                      onChange={(e) => set({ false_label: e.target.value || undefined })}
                      placeholder="OFF, Other, Stopped..."
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={styles.fieldLabel}>Fallback color</label>
                    <input
                      className="sd-builder-runtime-color is-wide"
                      type="color"
                      value={cfg.false_color || '#FF453A'}
                      onChange={(e) => set({ false_color: e.target.value })}
                    />
                  </div>
                </div>
              )}

              <div style={styles.fieldRow}>
                <div style={{ flex: 1 }}>
                  <label style={styles.fieldLabel}>Time span</label>
                  <select
                    style={styles.input}
                    value={cfg.range_hours || 168}
                    onChange={(e) => set({ range_hours: Number(e.target.value) })}
                  >
                    <option value={24}>Last 1 day</option>
                    <option value={168}>Last 7 days</option>
                    <option value={720}>Last 1 month</option>
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={styles.fieldLabel}>Bucket size</label>
                  <select
                    style={styles.input}
                    value={cfg.bucket_size || 'auto'}
                    onChange={(e) => set({ bucket_size: e.target.value as WidgetConfig['bucket_size'] })}
                  >
                    <option value="auto">Auto</option>
                    <option value="hour">Hourly</option>
                    <option value="day">Daily</option>
                  </select>
                </div>
              </div>

              <div className="sd-builder-config-note">
                Rules are evaluated from top to bottom. Unmatched readings can become the fallback label, while missing readings are labeled No telemetry so the chart does not guess.
              </div>
            </>
          )}

          {isConsumption && (
            <>
              <label style={styles.fieldLabel}>Consumption mode</label>
              <select
                style={styles.input}
                value={cfg.consumption_mode || 'tank_drawdown'}
                onChange={(e) => set({ consumption_mode: e.target.value as WidgetConfig['consumption_mode'] })}
              >
                {CONSUMPTION_MODE_OPTIONS.map((option) => (
                  <option key={option.mode} value={option.mode}>{option.label}</option>
                ))}
              </select>
              <div className="sd-builder-config-note">
                {CONSUMPTION_MODE_OPTIONS.find((option) => option.mode === (cfg.consumption_mode || 'tank_drawdown'))?.summary}
              </div>
              {rateModeWarning && (
                <div className="sd-builder-config-note is-warning">
                  {rateModeWarning}
                </div>
              )}

              <div style={styles.fieldRow}>
                <div style={{ flex: 1 }}>
                  <label style={styles.fieldLabel}>Output unit</label>
                  <input
                    style={styles.input}
                    value={cfg.unit || ''}
                    onChange={(e) => set({ unit: e.target.value })}
                    placeholder="L, kWh, m3..."
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={styles.fieldLabel}>Range</label>
                  <select
                    style={styles.input}
                    value={cfg.range_hours || 24}
                    onChange={(e) => set({ range_hours: Number(e.target.value) })}
                  >
                    <option value={6}>Last 6 hours</option>
                    <option value={24}>Last 24 hours</option>
                    <option value={72}>Last 3 days</option>
                    <option value={168}>Last 7 days</option>
                    <option value={720}>Last 1 month</option>
                  </select>
                </div>
              </div>

              <div style={styles.fieldRow}>
                <div style={{ flex: 1 }}>
                  <label style={styles.fieldLabel}>Tank capacity</label>
                  <input
                    style={styles.input}
                    type="number"
                    min={0}
                    value={cfg.tank_capacity ?? ''}
                    onChange={(e) => set({ tank_capacity: e.target.value === '' ? undefined : Number(e.target.value) })}
                    placeholder="e.g. 14000"
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={styles.fieldLabel}>Max daily use</label>
                  <input
                    style={styles.input}
                    type="number"
                    min={0}
                    value={cfg.max_daily_consumption ?? ''}
                    onChange={(e) => set({ max_daily_consumption: e.target.value === '' ? undefined : Number(e.target.value) })}
                    placeholder="e.g. 3000"
                  />
                </div>
              </div>

              <div style={styles.fieldRow}>
                <div style={{ flex: 1 }}>
                  <label style={styles.fieldLabel}>Valid minimum</label>
                  <input
                    style={styles.input}
                    type="number"
                    value={cfg.min_valid ?? ''}
                    onChange={(e) => set({ min_valid: e.target.value === '' ? undefined : Number(e.target.value) })}
                    placeholder="0"
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={styles.fieldLabel}>Valid maximum</label>
                  <input
                    style={styles.input}
                    type="number"
                    min={0}
                    value={cfg.max_valid ?? ''}
                    onChange={(e) => set({ max_valid: e.target.value === '' ? undefined : Number(e.target.value) })}
                    placeholder="defaults to capacity"
                  />
                </div>
              </div>

              <div style={styles.fieldRow}>
                <div style={{ flex: 1 }}>
                  <label style={styles.fieldLabel}>Max gap minutes</label>
                  <input
                    style={styles.input}
                    type="number"
                    min={0}
                    value={cfg.max_gap_minutes ?? ''}
                    onChange={(e) => set({ max_gap_minutes: e.target.value === '' ? undefined : Number(e.target.value) })}
                    placeholder="e.g. 60"
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={styles.fieldLabel}>Bucket sanity</label>
                  <input style={styles.input} value="Backend quality checks" disabled readOnly />
                </div>
              </div>

              <div className="sd-builder-config-note">
                Leave tank sanity fields blank to let the backend infer capacity and limits from telemetry metadata/history. Supplying the real capacity makes anomaly flags stronger.
              </div>

              {(cfg.consumption_mode || 'tank_drawdown') === 'tank_drawdown' && (
                <div style={styles.fieldRow}>
                  <div style={{ flex: 1 }}>
                    <label style={styles.fieldLabel}>Refill threshold</label>
                    <input
                      style={styles.input}
                      type="number"
                      value={cfg.refill_threshold ?? ''}
                      onChange={(e) => set({ refill_threshold: e.target.value === '' ? undefined : Number(e.target.value) })}
                      placeholder="backend default 1"
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={styles.fieldLabel}>Aggregation</label>
                    <input style={styles.input} value="Raw recent readings" disabled readOnly />
                  </div>
                </div>
              )}

              {(cfg.consumption_mode || 'tank_drawdown') === 'cumulative_counter' && (
                <div style={styles.fieldRow}>
                  <div style={{ flex: 1 }}>
                    <label style={styles.fieldLabel}>Reset threshold</label>
                    <input
                      style={styles.input}
                      type="number"
                      value={cfg.counter_reset_threshold ?? ''}
                      onChange={(e) => set({ counter_reset_threshold: e.target.value === '' ? undefined : Number(e.target.value) })}
                      placeholder="backend default 1"
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={styles.fieldLabel}>Aggregation</label>
                    <input style={styles.input} value="Raw recent readings" disabled readOnly />
                  </div>
                </div>
              )}

              {(cfg.consumption_mode || 'tank_drawdown') === 'rate_total' && (
                <div style={styles.fieldRow}>
                  <div style={{ flex: 1 }}>
                    <label style={styles.fieldLabel}>Rate basis</label>
                    <select
                      style={styles.input}
                      value={cfg.rate_time_unit || 'hour'}
                      onChange={(e) => set({ rate_time_unit: e.target.value as WidgetConfig['rate_time_unit'] })}
                    >
                      <option value="hour">Metric is per hour</option>
                      <option value="minute">Metric is per minute</option>
                      <option value="second">Metric is per second</option>
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={styles.fieldLabel}>Aggregation</label>
                    <input style={styles.input} value="Raw recent readings" disabled readOnly />
                  </div>
                </div>
              )}

              <div className="sd-builder-config-note">
                Consumption is calculated by the backend aggregate endpoint across the selected range. Tank refills and counter resets are excluded from consumed totals.
              </div>
            </>
          )}

          {isNumericMetric && usesMetric && !isRuntimeMode && !isConsumption && (
            <div style={styles.fieldRow}>
              <div style={{ flex: 1 }}>
                <label style={styles.fieldLabel}>Unit</label>
                <input
                  style={styles.input}
                  value={cfg.unit || ''}
                  onChange={(e) => set({ unit: e.target.value })}
                  placeholder="°C, %, bar..."
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={styles.fieldLabel}>Aggregation</label>
                <select
                  style={styles.input}
                  value={cfg.aggregation || 'avg'}
                  onChange={(e) => set({ aggregation: e.target.value as WidgetConfig['aggregation'] })}
                >
                  <option value="avg">Average</option>
                  <option value="min">Min</option>
                  <option value="max">Max</option>
                  <option value="last">Last</option>
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={styles.fieldLabel}>Range</label>
                <select
                  style={styles.input}
                  value={cfg.range_hours || 24}
                  onChange={(e) => set({ range_hours: Number(e.target.value) })}
                >
                  <option value={6}>Last 6 hours</option>
                  <option value={24}>Last 24 hours</option>
                  <option value={72}>Last 3 days</option>
                  <option value={168}>Last 7 days</option>
                  <option value={720}>Last 1 month</option>
                </select>
              </div>
            </div>
          )}

          {isThresholdTrend && (
            <>
              <label style={styles.fieldLabel}>Threshold guide lines</label>
              <div style={styles.fieldRow}>
                <div style={{ flex: 1 }}>
                  <label style={{ ...styles.fieldLabel, fontSize: '0.7rem' }}>Warn</label>
                  <input
                    style={styles.input}
                    type="number"
                    value={cfg.thresholds?.warn ?? ''}
                    onChange={(e) => set({ thresholds: { ...cfg.thresholds, warn: e.target.value === '' ? undefined : Number(e.target.value) } })}
                    placeholder="e.g. 75"
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ ...styles.fieldLabel, fontSize: '0.7rem' }}>Critical</label>
                  <input
                    style={styles.input}
                    type="number"
                    value={cfg.thresholds?.crit ?? ''}
                    onChange={(e) => set({ thresholds: { ...cfg.thresholds, crit: e.target.value === '' ? undefined : Number(e.target.value) } })}
                    placeholder="e.g. 90"
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ ...styles.fieldLabel, fontSize: '0.7rem' }}>Direction</label>
                  <select
                    style={styles.input}
                    value={cfg.threshold_direction || 'above'}
                    onChange={(e) => set({ threshold_direction: e.target.value as 'below' | 'above' })}
                  >
                    <option value="above">Above threshold</option>
                    <option value="below">Below threshold</option>
                  </select>
                </div>
              </div>
              <div className="sd-builder-config-note">
                Warn and Critical draw guide lines on the chart. Direction explains whether high or low values should be treated as risky.
              </div>
            </>
          )}

          {type === 'gauge' && (
            <>
              <label style={styles.fieldLabel}>Gauge Max</label>
              <input
                style={styles.input}
                type="number"
                value={cfg.max ?? ''}
                onChange={(e) => set({ max: e.target.value === '' ? undefined : Number(e.target.value) })}
                placeholder="e.g. 100"
              />
              <div className="sd-builder-config-note">
                Gauge max defines the full-scale value. Use the same engineering unit as the selected metric.
              </div>

              <label style={{ ...styles.fieldLabel, marginTop: '0.5rem' }}>Color bands</label>
              <div style={{ ...styles.fieldRow, gap: '0.5rem', marginBottom: '0.5rem' }}>
                {GAUGE_BANDS.map((band) => {
                  const defaults = defaultGaugeColors(cfg.gauge_reverse);
                  return (
                    <label key={band.key} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem' }}>
                      <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>{band.label}</span>
                      <input
                        type="color"
                        style={{ width: '100%', height: '32px', border: 'none', borderRadius: '6px', cursor: 'pointer', background: 'transparent' }}
                        value={cfg.gauge_colors?.[band.key] ?? defaults[band.key]}
                        onChange={(e) => {
                          const base = defaultGaugeColors(cfg.gauge_reverse);
                          set({
                            gauge_colors: { ...base, ...cfg.gauge_colors, [band.key]: e.target.value },
                          });
                        }}
                      />
                    </label>
                  );
                })}
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem', color: 'var(--text-secondary)', cursor: 'pointer', marginBottom: '0.75rem' }}>
                <input
                  type="checkbox"
                  checked={!!cfg.gauge_reverse}
                  onChange={(e) => set({ gauge_reverse: e.target.checked })}
                />
                Reverse colors (low values green, high values red)
              </label>

              <label style={styles.fieldLabel}>Threshold alarms</label>
              <div style={styles.fieldRow}>
                <div style={{ flex: 1 }}>
                  <label style={{ ...styles.fieldLabel, fontSize: '0.7rem' }}>Warn</label>
                  <input
                    style={styles.input}
                    type="number"
                    value={cfg.thresholds?.warn ?? ''}
                    onChange={(e) => set({ thresholds: { ...cfg.thresholds, warn: e.target.value === '' ? undefined : Number(e.target.value) } })}
                    placeholder="e.g. 75"
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ ...styles.fieldLabel, fontSize: '0.7rem' }}>Critical</label>
                  <input
                    style={styles.input}
                    type="number"
                    value={cfg.thresholds?.crit ?? ''}
                    onChange={(e) => set({ thresholds: { ...cfg.thresholds, crit: e.target.value === '' ? undefined : Number(e.target.value) } })}
                    placeholder="e.g. 90"
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ ...styles.fieldLabel, fontSize: '0.7rem' }}>Direction</label>
                  <select
                    style={styles.input}
                    value={cfg.threshold_direction || 'below'}
                    onChange={(e) => set({ threshold_direction: e.target.value as 'below' | 'above' })}
                  >
                    <option value="below">Below threshold</option>
                    <option value="above">Above threshold</option>
                  </select>
                </div>
              </div>
            </>
          )}

          {type === 'tank' && (
            <>
              <label style={styles.fieldLabel}>Tank Max</label>
              <input
                style={styles.input}
                type="number"
                value={cfg.max ?? ''}
                onChange={(e) => set({ max: e.target.value === '' ? undefined : Number(e.target.value) })}
                placeholder="e.g. 2000"
              />
              <div className="sd-builder-config-note">
                Tank max is the capacity used to calculate percentage fill and animated liquid height.
              </div>

              <label style={{ ...styles.fieldLabel, marginTop: '0.5rem' }}>Fill color</label>
              <input
                type="color"
                style={{ width: '100%', height: '32px', border: 'none', borderRadius: '6px', cursor: 'pointer', background: 'transparent' }}
                value={cfg.tank_fill_color || '#3b82f6'}
                onChange={(e) => set({ tank_fill_color: e.target.value })}
              />

              <label style={{ ...styles.fieldLabel, marginTop: '0.5rem' }}>Visual thresholds</label>
              <div style={styles.fieldRow}>
                <div style={{ flex: 1 }}>
                  <label style={{ ...styles.fieldLabel, fontSize: '0.7rem' }}>Low %</label>
                  <input
                    style={styles.input}
                    type="number"
                    value={cfg.tank_low_threshold ?? 25}
                    onChange={(e) => set({ tank_low_threshold: e.target.value === '' ? undefined : Number(e.target.value) })}
                    placeholder="e.g. 25"
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ ...styles.fieldLabel, fontSize: '0.7rem' }}>High %</label>
                  <input
                    style={styles.input}
                    type="number"
                    value={cfg.tank_high_threshold ?? 90}
                    onChange={(e) => set({ tank_high_threshold: e.target.value === '' ? undefined : Number(e.target.value) })}
                    placeholder="e.g. 90"
                  />
                </div>
              </div>

              <label style={{ ...styles.fieldLabel, marginTop: '0.5rem' }}>Threshold alarms</label>
              <div style={styles.fieldRow}>
                <div style={{ flex: 1 }}>
                  <label style={{ ...styles.fieldLabel, fontSize: '0.7rem' }}>Warn</label>
                  <input
                    style={styles.input}
                    type="number"
                    value={cfg.thresholds?.warn ?? ''}
                    onChange={(e) => set({ thresholds: { ...cfg.thresholds, warn: e.target.value === '' ? undefined : Number(e.target.value) } })}
                    placeholder="e.g. 500"
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ ...styles.fieldLabel, fontSize: '0.7rem' }}>Critical</label>
                  <input
                    style={styles.input}
                    type="number"
                    value={cfg.thresholds?.crit ?? ''}
                    onChange={(e) => set({ thresholds: { ...cfg.thresholds, crit: e.target.value === '' ? undefined : Number(e.target.value) } })}
                    placeholder="e.g. 250"
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ ...styles.fieldLabel, fontSize: '0.7rem' }}>Direction</label>
                  <select
                    style={styles.input}
                    value={cfg.threshold_direction || 'below'}
                    onChange={(e) => set({ threshold_direction: e.target.value as 'below' | 'above' })}
                  >
                    <option value="below">Below threshold</option>
                    <option value="above">Above threshold</option>
                  </select>
                </div>
              </div>
            </>
          )}

          {type === 'gps_map' && (
            <>
              <label style={styles.fieldLabel}>Location metric (optional)</label>
              <input
                style={styles.input}
                value={cfg.location_metric || ''}
                onChange={(e) => set({ location_metric: e.target.value || undefined })}
                placeholder="e.g. location or gps"
              />
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '-0.25rem', marginBottom: '0.5rem' }}>
                If set, value should be &quot;lat,lng&quot; or JSON {`{lat,lng}`}. Otherwise separate lat/lng metrics are used.
              </div>

              <label style={styles.fieldLabel}>Latitude metric</label>
              <input
                style={styles.input}
                value={cfg.latitude_metric || 'latitude'}
                onChange={(e) => set({ latitude_metric: e.target.value || undefined })}
                placeholder="e.g. latitude"
              />
              <div className="sd-builder-config-note">
                Metric key containing decimal latitude values.
              </div>

              <label style={styles.fieldLabel}>Longitude metric</label>
              <input
                style={styles.input}
                value={cfg.longitude_metric || 'longitude'}
                onChange={(e) => set({ longitude_metric: e.target.value || undefined })}
                placeholder="e.g. longitude"
              />
              <div className="sd-builder-config-note">
                Metric key containing decimal longitude values.
              </div>

              <label style={{ ...styles.fieldLabel, marginTop: '0.5rem' }}>Trail color</label>
              <input
                type="color"
                style={{ width: '100%', height: '32px', border: 'none', borderRadius: '6px', cursor: 'pointer', background: 'transparent' }}
                value={cfg.trail_color || '#2DD4A8'}
                onChange={(e) => set({ trail_color: e.target.value })}
              />

              <label style={{ ...styles.fieldLabel, marginTop: '0.5rem' }}>History range</label>
              <select
                style={styles.input}
                value={cfg.range_hours || 24}
                onChange={(e) => set({ range_hours: Number(e.target.value) })}
              >
                <option value={6}>Last 6 hours</option>
                <option value={24}>Last 24 hours</option>
                <option value={72}>Last 3 days</option>
                <option value={168}>Last 7 days</option>
              </select>
            </>
          )}

          {type === 'kpi' && (
            <div style={styles.fieldRow}>
              <div style={{ flex: 1 }}>
                <label style={styles.fieldLabel}>Warn below</label>
                <input
                  style={styles.input}
                  type="number"
                  value={cfg.thresholds?.warn ?? ''}
                  onChange={(e) => set({ thresholds: { ...cfg.thresholds, warn: e.target.value === '' ? undefined : Number(e.target.value) } })}
                  placeholder="e.g. 20"
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={styles.fieldLabel}>Crit below</label>
                <input
                  style={styles.input}
                  type="number"
                  value={cfg.thresholds?.crit ?? ''}
                  onChange={(e) => set({ thresholds: { ...cfg.thresholds, crit: e.target.value === '' ? undefined : Number(e.target.value) } })}
                  placeholder="e.g. 10"
                />
              </div>
            </div>
          )}

          {type === 'text' && (
            <>
              <label style={styles.fieldLabel}>Text</label>
              <textarea
                style={{ ...styles.input, minHeight: '120px', resize: 'vertical' }}
                value={cfg.text || ''}
                onChange={(e) => set({ text: e.target.value })}
                placeholder="e.g. Shift notes, inspection instructions, or operator labels..."
              />
              <div className="sd-builder-config-note">
                Static note displayed on the dashboard. Use it for operational context, not live data.
              </div>
            </>
          )}

          <div className="sd-builder-modal-actions">
            <button type="button" className="sd-builder-button" onClick={onClose}>Cancel</button>
            <button type="submit" className="sd-builder-button is-primary">Save Widget</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles: Record<string, React.CSSProperties> = {
  topBar: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '0.75rem',
    marginBottom: '1.25rem',
    flexWrap: 'wrap',
  },
  topBarLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    flexWrap: 'wrap',
  },
  topBarActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    flexWrap: 'wrap',
  },
  select: {
    padding: '0.5rem 0.875rem',
    borderRadius: '10px',
    background: 'rgba(22, 25, 34, 0.8)',
    border: '1px solid rgba(255,255,255,0.06)',
    color: 'var(--text-primary)',
    fontSize: '0.85rem',
    outline: 'none',
    cursor: 'pointer',
  },
  defaultBadge: {
    fontSize: '0.65rem',
    fontWeight: 700,
    padding: '0.2rem 0.5rem',
    borderRadius: '4px',
    background: 'rgba(45,212,168,0.12)',
    color: '#2DD4A8',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  dirtyDot: {
    fontSize: '0.75rem',
    color: '#F5A623',
    fontWeight: 600,
  },
  alarmBadge: {
    fontSize: '0.6rem',
    fontWeight: 800,
    padding: '0.15rem 0.4rem',
    borderRadius: '4px',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  btn: {
    padding: '0.5rem 0.875rem',
    borderRadius: '10px',
    border: '1px solid rgba(255,255,255,0.08)',
    background: 'rgba(255,255,255,0.04)',
    color: 'var(--text-primary)',
    fontSize: '0.8rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  btnPrimary: {
    background: 'rgba(45,212,168,0.15)',
    border: '1px solid rgba(45,212,168,0.35)',
    color: '#2DD4A8',
  },
  btnDanger: {
    background: 'rgba(232,96,44,0.1)',
    border: '1px solid rgba(232,96,44,0.3)',
    color: '#e8602c',
  },
  btnDisabled: {
    opacity: 0.45,
    cursor: 'default',
  },
  emptyState: {
    padding: '3rem 2rem',
    textAlign: 'center',
    color: 'var(--text-primary)',
  },
  widgetCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    color: 'var(--text-primary)',
  },
  widgetHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem 0.75rem',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
    flexShrink: 0,
  },
  dragHandle: {
    cursor: 'grab',
    color: 'var(--text-muted)',
    fontSize: '0.9rem',
    userSelect: 'none',
  },
  widgetTitle: {
    flex: 1,
    fontSize: '0.8rem',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  widgetActions: {
    display: 'flex',
    gap: '0.25rem',
  },
  iconBtn: {
    padding: '0.1rem 0.4rem',
    borderRadius: '6px',
    border: '1px solid rgba(255,255,255,0.08)',
    background: 'rgba(255,255,255,0.03)',
    color: 'var(--text-secondary)',
    fontSize: '0.8rem',
    cursor: 'pointer',
    lineHeight: 1.4,
  },
  widgetBody: {
    flex: 1,
    minHeight: 0,
    padding: '0.5rem 0.75rem',
    display: 'flex',
    flexDirection: 'column',
  },
  placeholder: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--text-muted)',
    fontSize: '0.8rem',
    textAlign: 'center',
  },
  widgetError: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#ef4444',
    fontSize: '0.75rem',
    textAlign: 'center',
    padding: '0.5rem',
  },
  kpiWrap: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.25rem',
  },
  kpiValue: {
    fontSize: '2rem',
    fontWeight: 800,
    letterSpacing: '-0.02em',
  },
  kpiUnit: {
    fontSize: '0.9rem',
    fontWeight: 500,
    color: 'var(--text-muted)',
  },
  kpiSubtitle: {
    fontSize: '0.75rem',
    color: 'var(--text-muted)',
  },
  gaugeWrap: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  tankWrap: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    minHeight: 0,
  },
  tankValue: {
    fontSize: '1.25rem',
    fontWeight: 800,
    color: 'var(--text-primary)',
  },
  tankUnit: {
    fontSize: '0.75rem',
    color: 'var(--text-muted)',
    fontWeight: 500,
  },
  tankBody: {
    position: 'relative',
    width: '70px',
    height: '100px',
    border: '2px solid rgba(255,255,255,0.15)',
    borderRadius: '8px',
    overflow: 'hidden',
    background: 'rgba(255,255,255,0.03)',
  },
  tankFill: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    transition: 'height 0.8s ease, box-shadow 0.3s ease',
  },
  tankScale: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: '0.25rem 0.2rem',
    pointerEvents: 'none',
  },
  tankTick: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.2rem',
  },
  tankTickLabel: {
    fontSize: '0.55rem',
    color: 'var(--text-muted)',
    width: '18px',
    textAlign: 'right',
  },
  tankTickLine: {
    flex: 1,
    height: '1px',
    background: 'rgba(255,255,255,0.1)',
  },
  tankPercent: {
    fontSize: '0.7rem',
    color: 'var(--text-muted)',
    fontWeight: 600,
  },
  gpsMapWrap: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    borderRadius: '10px',
    overflow: 'hidden',
    background: 'rgba(0,0,0,0.2)',
  },
  stateBadge: {
    fontSize: '1.25rem',
    fontWeight: 800,
    padding: '0.4rem 1.25rem',
    borderRadius: '999px',
    border: '1px solid',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  alertList: {
    flex: 1,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.35rem',
  },
  alertRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '0.5rem',
    fontSize: '0.72rem',
    padding: '0.3rem 0.4rem',
    borderRadius: '6px',
    background: 'rgba(245,166,35,0.06)',
    border: '1px solid rgba(245,166,35,0.12)',
  },
  alertTime: {
    color: 'var(--text-muted)',
    whiteSpace: 'nowrap',
  },
  alertDevice: {
    color: '#F5A623',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  alertMsg: {
    color: 'var(--text-secondary)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  statusGrid: {
    flex: 1,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.3rem',
  },
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '0.78rem',
    padding: '0.25rem 0.2rem',
  },
  statusDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  statusName: {
    flex: 1,
    color: 'var(--text-primary)',
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  statusLocation: {
    color: 'var(--text-muted)',
    fontSize: '0.65rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  statusBadge: {
    fontSize: '0.6rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    padding: '0.15rem 0.4rem',
    borderRadius: '4px',
    border: '1px solid',
    whiteSpace: 'nowrap',
  },
  statusSeen: {
    color: 'var(--text-muted)',
    fontSize: '0.7rem',
    whiteSpace: 'nowrap',
  },
  textWidget: {
    flex: 1,
    whiteSpace: 'pre-line',
    color: 'var(--text-secondary)',
    fontSize: '0.85rem',
    overflowY: 'auto',
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.65)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '1rem',
  },
  modalCard: {
    background: 'var(--bg-secondary, #161922)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '14px',
    padding: '1.5rem',
    width: '100%',
    maxWidth: '760px',
    maxHeight: '85vh',
    overflowY: 'auto',
    color: 'var(--text-primary)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
  },
  modalTitle: {
    margin: 0,
    fontSize: '1.05rem',
    fontWeight: 700,
  },
  templateGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))',
    gap: '1rem',
  },
  templateCard: {
    padding: '1rem',
    display: 'flex',
    flexDirection: 'column',
  },
  builtinBadge: {
    fontSize: '0.6rem',
    fontWeight: 700,
    padding: '0.2rem 0.45rem',
    borderRadius: '4px',
    background: 'rgba(59,130,246,0.15)',
    color: '#3b82f6',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    flexShrink: 0,
  },
  templateIndustry: {
    fontSize: '0.7rem',
    color: '#2DD4A8',
    fontWeight: 600,
    marginTop: '0.25rem',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  templateDesc: {
    fontSize: '0.75rem',
    color: 'var(--text-muted)',
    margin: '0.5rem 0',
    flex: 1,
  },
  fieldLabel: {
    display: 'block',
    fontSize: '0.7rem',
    fontWeight: 600,
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    margin: '0.875rem 0 0.35rem 0',
  },
  fieldRow: {
    display: 'flex',
    gap: '0.75rem',
  },
  input: {
    width: '100%',
    padding: '0.55rem 0.75rem',
    borderRadius: '10px',
    border: '1px solid rgba(255,255,255,0.08)',
    background: 'rgba(255,255,255,0.03)',
    color: 'var(--text-primary)',
    fontSize: '0.85rem',
    outline: 'none',
    boxSizing: 'border-box',
  },
  typePicker: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
    gap: '0.5rem',
  },
  typeBtn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.3rem',
    padding: '0.6rem 0.4rem',
    borderRadius: '10px',
    border: '1px solid',
    fontSize: '0.7rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  deviceCheckList: {
    maxHeight: '160px',
    overflowY: 'auto',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '10px',
    padding: '0.5rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  deviceCheckRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '0.82rem',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
  },
};
